import { IDescription, IName, INewDescription, INewName } from '../interfaces/global.interfaces';
import { Context } from '../models/context';
import { DataLoyalty } from '../models/data.interface';
import { TranslationMap } from './localization-service.interface';

interface IDataMapping {
    keyword: string,
    data: string | undefined
}

export class LocalizationService {
    private readonly _context: Context;

    constructor(context: Context) {
        this._context = context;
    }

    public getSupportedLanguages(): string[] {
        return ["en-US", "es-MX"];
    }

    /**
     * Returns the normalized language code for the provided language or the current context language.
     * @param {string} [languageCode] - The language code to normalize.
     * @returns {string} The normalized language code.
     */
    private _getNormalizedLanguageCode(languageCode?: string): string {
        const selectedLanguageCode = languageCode ?? this._context.getProperty("languageCode");
        return LocalizationService.normalizeLanguageCode(selectedLanguageCode.toLowerCase());
    }

    /**
     * Returns the name based on the culture code and name array provided.
     * It loops through the names array and returns the name that matches the current language code.
     * @param {Array<{CULT: string, NAM: string}>} names - An array of objects containing the culture code and name.
     * @returns {string} - The name in the current language code.
     */
    public GetName(names: IName[], languageCode?: string): string {
        const normalizedLanguageCode = this._getNormalizedLanguageCode(languageCode);
        return names ? (names.find(n => LocalizationService.normalizeLanguageCode(n.CULT.toLowerCase()) === normalizedLanguageCode)?.NAM || "") : "";
    }

    /**
     * Returns the localized text that matches the provided language code.
     * Uses the current context language when no language code is provided.
     * @param {INewName[]} names - The localized name entries to search.
     * @param {string} [languageCode] - The language code to match.
     * @returns {string} The matching localized text, or an empty string when no match is found.
     */
    public GetNewName(names: INewName[], languageCode?: string): string {
        const normalizedLanguageCode = this._getNormalizedLanguageCode(languageCode);
        return names ? (names.find(n => LocalizationService.normalizeLanguageCode(n.lc.toLowerCase()) === normalizedLanguageCode)?.text || "") : "";
    }

    /**
     * Returns the description based on the culture code and description array provided.
     * It loops through the names array and returns the description that matches the current language code.
     * @param {Array<{CULT: string, DESC: string}>} descriptions - An array of objects containing the culture code and description.
     * @returns {string} - The description in the current language code.
     */
    public GetDescription(descriptions: IDescription[], languageCode?: string): string {
        const normalizedLanguageCode = this._getNormalizedLanguageCode(languageCode);
        return descriptions ? (descriptions.find(d => LocalizationService.normalizeLanguageCode(d.CULT.toLowerCase()) === normalizedLanguageCode)?.DSCR || "") : "";
    }

    /**
     * Returns the text based on the culture code and name array provided.
     * It loops through the names array and returns the name that matches the current language code.
     * @param {Array<{lc: string, text: string}>} names - An array of objects containing the culture code and name.
     * @returns {string} - The text in the current language code.
     */
    public GetLocalized(names: INewName[] | INewDescription[], languageCode?: string): string {
        const normalizedLanguageCode = this._getNormalizedLanguageCode(languageCode);
        return names ? (names.find(n => LocalizationService.normalizeLanguageCode(n.lc.toLowerCase()) === normalizedLanguageCode)?.text || "") : "";
    }

    /**
     * Returns the localized loyalty unit name for the current language.
     * Falls back to the localized default value for "Points" when no custom unit name exists.
     * @param {DataLoyalty} loyaltyInfo - The loyalty configuration containing localized unit names.
     * @returns {string} The localized loyalty unit name or the default "Points" label.
     */
    public getLoyaltyUnitName(loyaltyInfo: DataLoyalty): string {
        const name = this.GetNewName(loyaltyInfo.adora?.unitsName ?? [], this._context.getProperty("languageCode"));
        return name ? name : this.names("Points");
    }

    /**
     * Returns the normalized language code
     * @param {string} [languageCode=aOLO.Temp.languageCode] - The language code to use for retrieving the name.
     * @returns {string} The name corresponding to the provided `name` and `languageCode`. If no name is found, it returns "undefined".
     */
    public static normalizeLanguageCode(languageCode: string): string {
        switch (languageCode.toLowerCase()) {
            case 'en-us':
                return 'en-US';
            case 'es-mx':
            case 'sp-mx':
                return 'es-MX';
            default:
                return languageCode;
        }
    }

    /**
     * Returns the name corresponding to the provided `name` and `languageCode`.
     * @param {string} name - The name to retrieve.
     * @returns {string} The name corresponding to the provided `name` and `languageCode`. If no name is found, it returns "undefined".
     */
    public names(name: string, languageCode?: string, ...params: (string | number)[]): string {
        languageCode = !languageCode ? LocalizationService.normalizeLanguageCode(this._context.getProperty("languageCode"))
            : LocalizationService.normalizeLanguageCode(languageCode);

        const languageTranslations = LocalizationService._translations[languageCode];
        //const phoneFormatLanguage = translations[NormalizeLanguageCode(aOLO.storeInfo?.Country?.DefaultCultureCode || "en-us")];

        if (languageTranslations) {
            let translation = languageTranslations[name];

            //const formattedLocalPhoneNumber = phoneFormatLanguage["PhoneFormat"]
            if (translation) {
                // Replace year placeholder
                translation = translation.replace(/%year%/g, new Date().getFullYear().toString());
                //.replace(/%storePhoneNumber%/g, Util.formatPhoneNumber(aOLO.storeInfo?.Phone, formattedLocalPhoneNumber));

                params.forEach((param, index) => {
                    const regex = new RegExp(`\\{${index}\\}`, "g");
                    translation = translation.replace(regex, String(param));
                });

                return translation;
            }
        }

        return 'undefined';
    }

    /**
     * Returns the localized name from a JSON string based on the specified language code.
     * @param nameJson - A JSON string containing the names for different languages.
     * @returns The localized name from the JSON string, or "undefined" if the name is not found.
     */
    public namesJson(nameJson: string): string {
        const languageCode = LocalizationService.normalizeLanguageCode(this._context.getProperty("languageCode"));

        const names = JSON.parse(nameJson) as IName[];
        const name = names.find(n => LocalizationService.normalizeLanguageCode(n.CULT) === languageCode)?.NAM;

        return name || "undefined";
    }

    /**
     * Sets the page language and updates all elements with localization attributes accordingly.
     * @returns void
     */
    public setPageLanguage(): void {
        //localStorage.setItem("languageCode", languageCode);
        //globalThis.aOLO.Temp.languageCode = languageCode;
        //document.body.lang = NormalizeLanguageCode(languageCode).split('-')[0];
        const languageCode = LocalizationService.normalizeLanguageCode(this._context.getProperty("languageCode"));

        this._applyLocalizedText("ltag", (el, text) => { el.innerHTML = text; });
        this._applyLocalizedText("ltagp", (el, text) => { el.setAttribute("placeholder", text); });
        this._applyLocalizedText("ltaga", (el, text) => { el.setAttribute("aria-label", text); });

        const ltagJson = Array.from(document.querySelectorAll('[ltagj]')) as HTMLElement[];

        for (const el of ltagJson) {
            let ltagj = el.getAttribute("ltagj");
            if (ltagj) {
                const names = JSON.parse(decodeURIComponent(ltagj));

                if (languageCode === "en-US") {
                    let nam = names.find((x: INewName) => x.lc === "en-US");
                    if (nam) {
                        el.innerHTML = this._stringMapData(nam.text);
                        continue;
                    }

                    nam = names.find((x: IName) => x.CULT === "en-us");
                    if (nam) {
                        el.innerHTML = nam.NAM ? this._stringMapData(nam.NAM) : this._stringMapData(nam.DSCR);
                        continue;
                    }
                }

                if (languageCode == "es-MX") {
                    let nam = names.find((x: INewName) => x.lc === "es-MX");
                    if (nam) {
                        el.innerHTML = this._stringMapData(nam.text);
                        continue;
                    }

                    nam = names.find((x: IName) => x.CULT === "sp-mx");
                    if (nam) {
                        el.innerHTML = nam.NAM ? this._stringMapData(nam.NAM) : this._stringMapData(nam.DSCR);
                        continue;
                    }
                }
            }
        }
    }

    /**
     * Applies the localized string for a localization attribute to every matching element.
     * @param {string} attribute - The localization attribute to look up (e.g. "ltag", "ltagp", "ltaga").
     * @param {(el: HTMLElement, text: string) => void} apply - Callback that writes the localized text onto the element.
     * @returns void
     */
    private _applyLocalizedText(attribute: string, apply: (el: HTMLElement, text: string) => void): void {
        document.querySelectorAll<HTMLElement>(`[${attribute}]`).forEach((el: HTMLElement) => {
            const key = el.getAttribute(attribute);
            if (key)
                apply(el, this._stringMapData(this.names(key)));
        });
    }

    /**
     * Asynchronously loads external translations from a remote `names.json` file
     * and merges them into the current application translation map.
     *
     * @returns {Promise<void>} A promise that resolves when translations are loaded and merged, or if the fetch fails silently.
     */
    public async loadAndMergeTranslationsAsync(): Promise<void> {
        try {
            const fullUrl = `${this._context.getProperty("publicStorageUrl")}/online/names.json?v=${Date.now()}`;
            const response = await fetch(fullUrl);
            if (!response.ok)
                return;

            const externalTranslations = await response.json();

            this._mergeTranslations(externalTranslations);
        } catch (error) {
            console.log(error);
        }
    }

    /**
     * Replaces predefined string keywords (e.g., `{{brand}}`) with mapped values.
     *
     * @param {string} instr - The input string containing optional placeholders.
     * @returns {string} The string with placeholder keywords replaced by mapped data.
     */
    private _stringMapData(instr: string): string {
        const keywords: IDataMapping[] =
            [
                { keyword: "{{brand}}", data: "" }
            ];

        let temp: string = instr;

        keywords.forEach(x => {
            if (temp.includes(x.keyword) && x.data)
                temp = temp.replace(x.keyword, x.data);
        });

        return temp;
    }

    /**
     * Merges the provided external translations with the existing translations.
     *
     * If the language already exists in the current translations, the new translations are merged with the existing ones.
     * If the language does not exist, the new translations are added.
     *
     * @param externalTranslations - An object containing external translations mapped by language codes.
     * @returns void
     */
    private _mergeTranslations(externalTranslations: TranslationMap | null | undefined): void {
        if (!externalTranslations) {
            console.error('External translations cannot be null');
            return;
        }

        Object.keys(externalTranslations).forEach(lang => {
            LocalizationService._translations[lang] = Object.assign({}, LocalizationService._translations[lang], externalTranslations[lang]);
        });
    }

    private static _translations: Record<string, Record<string, string>> = {
        'en-US': {
            '1stHalf': "1st Half",
            '2ndHalf': "2nd Half",
            '400': "An unexpected error occurred, please contact the store.",
            '401': "Unauthorized access.",
            '403': "Forbidden resource can't be accessed.",
            '500': "Internal server error.",
            '503': "Service unavailable.",
            'Abort': "Request was aborted by the server!",
            'ACCEPT_ALL': "ACCEPT ALL",
            'Account': "Account",
            'AccountDeleteSuccess': "Your account has been successfully deleted.",
            'AccountDeleteFailure': "Unable to delete your account.",
            'AccountDeleteConfirm': "Are you sure you want to delete your account? This action can't be undone.",
            'AccountUpdateSuccess': "Your contact information has been successfully updated.",
            'AccountUpdateFailure': "Unable to update your contact information.",
            'AccountChangePassword': "Account Change Password",
            'AccountConfirmDelete': "Account Confirm Delete",
            'AccountContact': "Account Contact",
            'Achieved': "Achieved",
            'AddMoreItems': "Add More Items",
            'AddNewCard': "Add New Card",
            'AddNewDeliveryAddress': "Add New Delivery Address",
            'AddPassword': "Add Password",
            'Added': "Added",
            'Address': "Address",
            'Address1': "Address1",
            'AddressDir': "Address",
            'Addresses': "Addresses",
            'AddressSaveError': "There was an error updating your address. Please try again.",
            'AddToDeal': "Add To Deal",
            'AddDealToOrder': "Add Deal To Order",
            'AddToFavorites': "Add to Favorites",
            'AddToOrder': "Add To Order",
            'AGE': "Are you ?? years old or older?",
            'Age13': "Yes, I am 13 years of age or older.",
            "AllergenInfo": "Allergen Info",
            'Allergy': "Allergy",
            'AllergyPreferences': "Allergies",
            'ALLOW_COOKIES_BODY': "We value your privacy. Cookies help improve performance and personalize your experience. Manage your preferences below.",
            'ALLOW_COOKIES_HEADER': "ALLOW COOKIES?",
            'CookiePreference': "Cookie Preference",
            'ALREADYAMEMBER': "ALREADY A MEMBER?",
            'AlreadyHaveAnAccount': "Already Have an Account?",
            'AmountDue': "Amount Due:",
            'AmountRedeemCurrencyApplyToOrder': "Enter the amount to apply to the order",
            'AmountToUse': "Amount to use",
            'And': "and",
            'APP_DOWNLOAD_BANNER': "App download banner",
            'APP_ICON_FOR': "App icon for {0}",
            'Applied': "Applied",
            'AppliedToOrder': "Applied to Order",
            'Apply': "Apply",
            'ApplyChanges': "Apply Changes",
            'ApplyGiftCard': "Apply Gift Card",
            'AppUpdateRequiredMessage': "A new version is ready. Update now to enjoy the latest features and improvements.",
            'April': "04 - April",
            'AprilOnly': "April",
            'AptUnit': "Apt / Unit / Room No",
            'AptSuiteRoom': "Apt / Suite / Room",
            'AptSuiteRoomPlaceholder': "Apt, Suite, Room #",
            'AptUnitSuite': "Apt/Unit/Suite",
            'AreYouSureSignOutOrderCleared': "Are you sure you want to sign out? Your order will be cleared.",
            'ARIA_DECREASE_QUANTITY': "Decrease quantity",
            'ARIA_INCREASE_QUANTITY': "Increase quantity",
            'ARIA_LEFT_HALF': "Left half",
            'ARIA_QUANTITY_SELECTOR': "Quantity selector",
            'ARIA_RIGHT_HALF': "Right half",
            'ARIA_SCROLL_DOWN': "Scroll down",
            'ARIA_SCROLL_LEFT': "Scroll left",
            'ARIA_SCROLL_RIGHT': "Scroll right",
            'ARIA_SCROLL_UP': "Scroll up",
            'ARIA_LOYALTY_REDEEM_TOGGLE': "Choose payment method: Pay With Dollars or Redeem with Points",
            'ARIA_LOYALTY_REDEEM_TOGGLE_LOCKED': "Redeem with Points unavailable. Not enough points",
            'ASAP': "ASAP",
            'ASAP_UNAVAILABLE': "Immediate ordering (ASAP) is currently unavailable.",
            'ASAPEstimatedTime': "ASAP (Est {{minutes}} minutes)",
            'August': "08 - August",
            'AugustOnly': "August",
            'AutocompleteText': "Enter 4+ characters for autocomplete...",
            'AutoSuggestionsAvailable': "Auto-suggestions available.",
            'AvailableAmount': "Available Amount:",
            'AVAILABLE_POINTS_TEMPLATE': "Available {{points_unit}}",
            'AVAILABLE_POINTS_LABEL_WITH_POINTS': "{{points}} Available {{points_unit}}",
            'AvailableRewardsPoints': "Available Rewards Points",
            'AwayFromNext': "away from your next",
            'Balance': "Balance",
            'BalanceAsOf': "Balance as of",
            'BankedCurrency': "Banked Currency",
            'BillAddr': "Billing Address:",
            'BS': "Bahamas (+1)",
            'Addr': "Address:",
            'BACK_TO_HOME': "BACK TO HOME",
            'BillStName': "Billing street name",
            'BillStNo': "Billing street No.",
            'BillZip': "Zip Code:",
            'Birthday': "Birthday",
            'BoxTopMsg': "Discount will be applied to your order as soon as all the required items are added to the order.",
            'BrowserNotSupported': "We're sorry for the inconvenience, but our website does not support Internet Explorer or<br>Safari versions older than version 14. These browsers are outdated or unsupported and may not<br>display all features of our site correctly. We highly recommend updating your browser to the<br>latest version or using a different browser such as Google Chrome, Mozilla Firefox, or<br>Microsoft Edge for the best experience on our site. Thank you for your understanding.",
            'BuildYourDeal': "Build Your Deal",
            'BusinessDayIsClosed': "Business day is closed.",
            'Call': "Call",
            'CallStore': "Please call the store @ %storePhoneNumber% to place your order.",
            'CalorieFractionMin': "?? cal. per one ??? ????",
            'CalorieFractionMinMax': "?? - ??? cal. per one ???? ?????",
            'CALORIES': "Calories",
            'CaloriesPerFraction': "{{calorie}} cal. per 1/{{serving}} {{servingName}}",
            'CaloriesPerServing': "{{calorie}} cal. / {{servingName}}",
            'CalorieServingMin': "?? cal. per ???",
            'CalorieServingMinMax': "?? - ??? cal. per ????",
            'Cancel': "Cancel",
            'CannotResolveAddress': "Cannot resolve address",
            'CannotUseCouponTotalZero': "Cannot apply reward when total is $0. Try adding something to your cart.",
            'CardNo': "Card Number:",
            'CardNumber': "Card Number",
            'CardholderName': "Card Holder Name",
            'CardOnFile': "Card on file",
            'CarMakeModelAndColor': "Car make, model, and color",
            'CarryoutLocation': "{{order_type_name}} Location",
            'CarryoutMethod': "{{order_type_name}} Method",
            'CART_ITEMS_REMOVED_NOTICE': "All items have been removed from your cart due to unavailability or delivery restrictions.",
            'CART_ITEMS_PARTIAL_UNAVAILABLE_STORE_SWITCH': "Some items in your cart are unavailable at the selected store or delivery address:<br>{{item_names}}Would you like to continue?<br>This will remove the unavailable items from your cart.",
            'CART_ITEMS_UNAVAILABLE_STORE_SWITCH': "All items in your cart are unavailable at the selected store.<br>Would you like to continue switching stores?<br>This will remove all items from your cart.",
            'CART_UPDATED_NOTICE': "Your Cart Has Been Updated",
            'Cart_RemovedItemNotice': "{{item_name}} was removed from your order.",
            'Cart_ScreenReader_RemovedItemNotice': "Removed {{item_name}}",
            'ITEM_PRICING_UNAVAILABLE': "Sorry, we couldn't price one or more of these items. Please try adding them again.",
            'ITEMS_NO_LONGER_AVAILABLE': "Item {0} is no longer available in selected size.",
            'Cash': "Cash",
            'CASH_ROUNDING_MESSAGE': "Cash Payments may be rounded to a {{roundingValue}} value.",
            'CCNotInte': "Please have your credit card and ID ready for the driver.",
            'CHALLENGE_PARTICIPATION_CONFIRMATION': "You are now participating in: {{challenge_name}}",
            'Challenges': "Challenges",
            'Change': "Change",
            'ChangeStore': "Change Store",
            'ChangeDeliveryAddress': "Change Delivery Address",
            'ChangeOrderDateTime': "Change Order Date & Time",
            'ChangeOrderType': "Change Order Type",
            'ChangeOrderTypeAndStore': "Change order type and store",
            'ChangePassword': "Change Password",
            'ChangeTime': "Change Time",
            'CharNotAllowed': "Your password contains characters that are not allowed.</br>The only allowed special characters are",
            'CHECKOUT': "CHECKOUT",
            'Checkout': "Checkout",
            'CheckOutTheMenuToFindSomethingYouLove': "Check out the menu to find something you love",
            'CHOOSE_1ST_HALF': "Choose 1st Half",
            'CHOOSE_2ND_HALF': "Choose 2nd Half",
            'ChooseMany': "Choose Many",
            'ChooseOne': "Choose One",
            'ChooseStore': "Choose Store",
            'City': "City",
            'Close': "Close",
            'CLOSE_BANNER': "Close banner",
            'Closed': "Closed",
            'ClosedForTheHoliday': "Closed for the holiday",
            'ClosedOpensAt': "Closed - Opens at {time} {weekday}",
            'ComingSoon': "Coming Soon",
            'Comment': "Comment",
            'CompleteIncompleteDiscounts': "Please complete your current discount's item criteria before adding more rewards.",
            'CompleteRequiredItems': "Complete Required Items",
            'Complete': "Complete",
            'CONFIRM_DELETE_ADDRESS': "Are you sure you want to delete {{address}}?",
            'ConfirmPassword': "Confirm Password",
            'ConfirmNewPassword': "Confirm New Password",
            'Conf1': "Your order submitted successfully.",
            'Conf2': "You will be informed by email as soon as the store confirms your order on ??.",
            'Conf3': "You will be informed by email as soon as the store confirms your order.",
            'Conf4': "Transaction #",
            'Conf5': "Order #",
            'Confirm': "Confirm",
            'ConfirmationPromiseTime': "{{time}} ({{minute_low}} - {{minute_high}} Minutes)",
            'Contact': "Contact",
            'ContactInformation': "Contact Information",
            'Contactless': "Contactless Delivery",
            'ContactlessInfo': "Contactless delivery is not available for cash payments.",
            'ContactUs': "Contact Us",
            'Continue': "Continue",
            'ContinueAsGuest': "Continue As Guest",
            'ContinueToPayment': "Continue To Payment",
            'Copyright': "© 2015 - %year% Adora POS, LLC. All rights reserved.",
            'CouldNotGetOrderNumber': "Could not get order number. Please try again.",
            'Countdown': "Countdown",
            'Country': "Country",
            'CountryCode': "Country Code",
            'CouponApplied': "Coupon Applied",
            'CouponCannotApplyMultiple': "This coupon cannot be applied multiple times.",
            'CouponFutureOrderExpire': "Coupon cannot be applied to order scheduled past its expiration date.",
            'CouponCode': "Coupon Code",
            'REMOVE_FUNDRAISER_FOR_PROMO_CODE': "To use a promotion code, please remove any applied fundraiser codes first.",
            'CouponDisclaimer': "Not valid with any other offers, discounts, fundraisers or promotions. Valid for take-out, dine-in and delivery. delivery charges apply.",
            'LOCATION_OFFER_UNAVAILABLE': "This offer is not available at this location.",
            'CouponNotQualified': "Coupon Not Qualified!",
            'CouponRedeemed': "Coupon has already been redeemed.",
            'CP_DeleteLoyProfileConfirmation': "Are you sure you want to delete your loyalty profile?<br>This cannot be undone.",
            'CPNLIMIT': "There is limit of ?? per order for selected coupon.",
            'CpnLimitOne': "Oops! Only one coupon per order can be redeemed at a time. Please try again.",
            'CPNMINAMT': "Minimum order amount of ?? is required for the selected coupon.",
            'CpnOTNotOffered': "Applied discounts are not offered on selected order type.  If continue is selected, the discount(s) will be removed.",
            'CreateAccount': "Create Profile",
            'CreateAccountConfirmationPage': "Create Profile",
            'CreateAFavorite': "Create a Favorite",
            'CreateAFavorite_BodyText': "Add any meal from recent orders to easily order it again.",
            'CreditCard': "Credit Card",
            'CreditCardEndingWith': "{{card_type}} ending with {{last_four}}",
            'CreditcardExpectCall': "You will receive a call from the store to get your credit card information.",
            'CreditcardPhotoID': "Please have the credit card and a valid photo ID present when receiving the order.",
            'CreditCardPreAuthNote': "Note: This credit card has pre-authorization for this purchase; the final payment will be charged in-store at pickup time.",
            'CreditCardPrepareInfo': "Credit Card (via phone)",
            'CreditCardUponDelivery': "Credit Card Upon Delivery",
            'CreditDebit': "Credit/Debit",
            'CreditHistory': "Credit History",
            'Curbside': "Curbside",
            'Curbside_CarMakeModel': "Car's make and model",
            'Curbside_ConfirmCarModel': "Please confirm your car's make, model and color.",
            'Curbside_EstimatedDistanceFromRestaurant': "Estimated distance from the store is ?? ???.",
            'Curbside_SelectStallNumber': "Please select your parking stall number.",
            'Curbside_StoreHasBeenNotified': "Your request has been sent to the store. We will bring your order to you as soon as it's ready.",
            'Curbside_StoreHasBeenNotifiedAlready': "The store is already notified.",
            'Curbside_StoreWillBeNorified': "The store will be notified that you are here.",
            'Curbside_LocationAccessDisabled': "Location access is disabled, so we're unable to estimate your distance from the store.",
            'CurbsideInfo': "Let us bring your order to your car! Please pull up to the white canopy. We will direct you where to park, and we'll bring your food out when ready. Must be paid by credit card at time of order. Thanks for helping us stay safe!",
            'Curbside_ConfirmationError': "There was an error confirming your curbside information. Please try again later.",
            'CurrentPassword': "Current Password",
            'Custom': "Custom",
            'CustomTip': "Custom Tip",
            'Customize': "Customize",
            'CustomizeItem': "Customize Item",
            'Date': "Date",
            'DateInPast': "Date must not be in the past.",
            'Day': "Day",
            'DealBuilder': "Deal Builder",
            'DealBuilderButtonDescription': "Select and customize the required items to build your deal and add it to your order.",
            'December': "12 - December",
            'DecemberOnly': "December",
            'DefaultAddress': "Make this my default address",
            'DefaultModifierUnavailable': "{0} is not available now.",
            'Delete': "Delete",
            'DeleteAccount': "Delete Account",
            'DeleteMyAccount': "Delete My Account",
            'DeletePaymentConfirm': "Are you sure you want to delete the card ending in",
            'DeletePaymentFailure': "Unable to delete your payment method.",
            'DeletePaymentSuccess': "Your payment method has been successfully deleted.",
            'DeleteProfile': "Delete Profile",
            'DeleteProfileMessage': "Are you sure you want to delete your profile? </br>This is a permanent deletion of all records in your profile including order history, </br>loyalty points, earned rewards, existing offers, and orders. </br></br> Enter your password to confirm you want to delete your profile.",
            'DeliverTo': "Deliver To",
            'Delivery': "Delivery",
            'DeliveryAddress': "Delivery Address",
            'DeliveryAddressOutsideAllZonesError': "Delivery is not available for the address provided as it is outside the delivery area of any location. Please choose a different address or select an alternative ordering option.",
            'DeliveryAddressIncompleteAddressError': "Enter a full street address with a house number (e.g., '123 Main St').",
            'DeliveryCharge': "Delivery Charge",
            'DeliveryInstructions': "Delivery Instructions",
            'DeliveryInstructionsPlaceholder': "E.g., use back door, gate code",
            'DeliveryOutLimitedTimeError': "Currently Only Offering Pre-Order",
            'DeliveryTime': "Delivery Time",
            'DeliveryTo': "{{order_type_name}} To",
            'DelLastAddr': "Cannot Delete. One address must remain on file.",
            'DietaryPreferences': "Dietary Preferences",
            'DigitalGC': "Digital Gift Card",
            'DineIn': "Dine In",
            'DineInAt': "Dine In at",
            'Discount': "Discount",
            'Discounted': "Discounted",
            'DiscountedTotal': "Discounted Total",
            'Discounts': "Discounts",
            'Donate': "Donate",
            'Donation': "Donation",
            'Done': "Done",
            'DontContinue': "Don't continue",
            'DontForget': "DON'T FORGET!",
            'DontHaveAnAccount': "Don't Have an Account?",
            'DontSave': "Don't Save",
            'DowngradedTo': "Downgraded to",
            'Due': "Due",
            'EarnedPTS': "Earned",
            'EarnedREW': "Earned",
            'EARN_REWARDS_ON_ORDER': "You'll earn <strong>{{points}} {{points_unit}}</strong> once you complete your order! Additional offers below.",
            'EARN_REWARDS_ON_ORDER_NO_ITEMS': "Add items to your cart to start earning {{points_unit}} on this order.",
            'EARN_REWARDS_SIGNUP': "Start earning {{points_unit}} to redeem towards your favorite menu items. You could earn <strong>{{points}} points</strong> for this order by signing up!",
            'EARN_REWARDS_SIGNUP_NO_ITEMS': "Add items to your cart and sign up to start earning {{points_unit}} toward your favorite menu items!",
            'Edit': "Edit",
            'EditContact': "Edit Contact",
            'EditPreferredDeliveryAddress': "Edit Preferred Delivery Address",
            'EditPreferredCarryOutStore': "Edit Preferred Carry Out Store",
            'EditingDiscountedItem': "Editing a discounted item will remove the discount. You may re-apply the discount after you have finished editing. Do you want to proceed with editing the item?",
            'Email': "Email",
            'EMAIL_ALREADY_EXISTS': "Email already exists.",
            'EmailAddress': "Email Address",
            'EmailNoMatch': "The e-mail addresses you entered do not match.",
            'EmailNotFound': "We did not find the email address '??' in our records.",
            'EmailOrPhoneNumber': "Email or Phone Number",
            'EmailPlaceHolder': "email@example.com",
            'EmptyDate': "Date must be not empty.",
            'EmptyMemberList': "You must add at least one member for a group order.",
            'EmptyTime': "Time must be not empty.",
            'EndingBalance': "Ending Balance",
            'EndPrivTerm': ".",
            'EnoughPoints': "You do not have enough points to redeem the selected item.",
            'ENTER_CODE': "Enter Code",
            'ENTER_FUNDRAISER_CODE': "Please enter a fundraiser code to apply.",
            'ENTER_FUNDRAISER_NAME': "Please enter the {{fundraiser_name}}",
            'EnterDeliveryAddress': "Enter Delivery Address",
            'EnterPasscode': "Enter Passcode",
            'EnterPasscodeExpiresInXMinutes': "Please enter the passcode texted to you.<br>Passcode expires in 15 minutes.",
            'EnterYourPasswordPlaceholder': "Enter your password",
            'ErrorGettingServerDate': "Error getting server date.",
            'Error_OrderExceededCashLimit': "Unfortunately, this order has exceeded our store's cash limit of {{money}}.<br>However, you can still proceed with payment using a credit card.<br>We apologize for any inconvenience this may cause.",
            'Error_ChangeOrderTypeCartError': "{{size}} {{item}} is unavailable for {{order_type}}.<br>Please remove it from your order before changing the order type.",
            'Error_ConfigurationGenericError': "The online ordering system is currently unavailable due to a configuration issue.<br>Please try again later or contact the store directly for assistance.<br>We apologize for the inconvenience.",
            'ERROR_COUPON_INELIGIBLE_FOR_USE_WITH_EVENT': "Coupons are ineligible for use with {{event_name}}",
            'Error_GiftCardBalanceIs0': "This gift card has a zero balance and cannot be used for payment.",
            'Error_HalfNotAvailableForSelectedSize': "The Half & Half option is not available for the selected size.<br>Please choose a different size or option.",
            'Error_ModifierIsNotBeingOfferedOnSize': "{{modifier}} is available for the following sizes: {{sizes}}.",
            'Error_NoItemsHaveBeenAddedToOrder': "You cannot proceed to checkout because no items have been added to your order.<br>Please add at least one item to continue.",
            'Error_NoPaymentAttachedToOrder': "No payment method was found for this order. Please add or reselect a payment method before completing checkout.",
            'Error_OrderTypeUnavailableAtStore': "{{order_type}} is not available at your selected store. Please choose a different store that supports this order type.",
            'ERROR_PARTICIPATE_EVENT_REMOVE_COUPON': "If you want to participate in {{event_name}}, please remove your applied coupons",
            'Error_ReachedMaxNumberOfModifiers': "You have reached the maximum number of toppings allowed. Please adjust your selection to continue.",
            'Error_ReachedMaxNumberOfModifiersOnItem': "You have reached the maximum number of modifiers allowed for this item.<br>Please review your selections.",
            'Error_ReservedTimeSlotExpired': "Your reserved time slot has expired.<br>Your order will now be fulfilled as soon as possible.<br>The estimated {{order_type_name}} time is approximately {{minutes}} minutes from the time of order placement.",
            'Error_ApplePayCannotStart': "Unable to start Apple Pay. Please try another payment method.",
            'Error_GooglePayCannotComplete': "Unable to complete Google Pay. Please try another payment method.",
            'Error_SavedCardNotProperlySelected': "We couldn't confirm your saved card. Don't worry, no payment was processed. Please reselect your card and try again.",
            'Error_SelectedModifierNoSizeSwitch': "{{modifier}} is not available for the {{size}} {{item}}.<br>Would you like to switch to a size where {{modifier}} is available?",
            'Error_SelectToppings1Half': "Please select a topping for the first half.<br>For example, choose a cheese topping for a cheese pizza.",
            'Error_SelectToppings2Half': "Please select a topping for the second half.<br>For example, choose a cheese topping for a cheese pizza.",
            'Error_UnableToFindProperSizeToApply': "We are unable to identify a suitable size for the selected item.<br>Please review your selection and try again.",
            'Error_CustomizeUnable': "Unable to customize this item. Please try again later.",
            'Error_SelectSize': "Select a size.",
            'Error_StoreClosed': "The store is currently closed and cannot accept orders at this time.<br>Please try again during business hours or contact the store directly for assistance.",
            'Estimated': "Estimated",
            'EstimatedDelivery': "Estimated Delivery",
            'EstimatedDeliveryTime': "Estimated Delivery Time",
            'EstimatedOrderTime': "Estimated Order Completion Time",
            'ETA_ORDER_TYPE_TIME_IS': "Estimated {{order_type}} Time is {{time}}",
            'ETA_TIME_WITH_MINUTES': "{{time}} ({{formatted_minutes}})",
            'ExistingCreditCard': "Existing Credit Card",
            'ExistingCustomerSignIn': "Existing customer sign in",
            'ExpDate': "Expiration Date:",
            'ExpirationDate': "Expiration Date",
            'Expired': "Expired",
            'Expires': "Expires",
            'ExtraToppings': "Extra Toppings",
            'FailUpdateInfo': "Unable to update info at the moment.",
            'Favorites': "Favorites",
            'FavoriteStores': "Favorite Stores",
            'FavoriteMenuItems': "Favorite Menu Items",
            'FavoriteLocations': "Favorite Locations",
            'FavoriteItem': "Favorite Item",
            'FavoriteItemModalDescription': "The item and all its customizations will be saved to your favorites after adding to the order cart.",
            'FavoriteItemNameRequiredError': "Favorite item name is required.",
            'FavoriteItemNameLengthError': "Favorite item name must be less than 100 characters.",
            'FavoriteItemMissingMod': "One or more toppings are not available at this location.",
            'FavoriteItemSave_Error': "There was an error saving the favorite item. Please try again.",
            'FavoriteItemSave_Success': "Favorite item has been saved.",
            'FavoriteItemUnavailable': "Item unavailable at this location.",
            'FavoriteItem_Delete_Error': "There was an error deleting the favorite item. Please try again.",
            'FavoriteItem_Delete_Confirm': "Are you sure you want to remove this item from your favorites?",
            'FavoriteObjectNone': "YOU HAVEN'T SAVED ANY FAVORITE {{object}} YET",
            'FavoriteObjectInstruction': "Tap the heart icon next to your favorite {{object}} to save them here for quick access.",
            'FavoriteOrders': "Favorite Orders",
            'favoriteStoreDelete_Error': "There was an error deleting the favorite store. Please try again.",
            'favoriteStoreSaveName_Error': "There was an error saving the favorite store. Please try again.",
            'favoriteStoreDelete_Confirm': "Are you sure you want to remove this store from your favorites?",
            'FavStore': "Make this store my favorite store",
            'February': "02 - February",
            'FebruaryOnly': "February",
            'feet': "feet",
            'Find': "Find",
            'FindStore': "Find a Store",
            'FinishCoupon': "Finish Coupon",
            'FinishX': "Please finish your order within ?? minutes.",
            'FirstHalf': "First Half",
            'FirstName': "First Name",
            'For': "for",
            'ForgotPassword': "Forgot Password",
            'ForgotPasswordEmailError': "Enter your email to reset your password.",
            'Foundation': "Foundation",
            'Franchising': "Franchising",
            'Friday': "Friday",
            'FulfillmentTime': "The fulfillment time for this order has been changed to As Soon As Possible, ??? time is approximately ?? minutes after your order has been placed.",
            'Fundraiser': "Fundraiser",
            'FundraiserCode': "Fundraiser Code",
            'FUNDRAISER_CODE_APPLIED': "Your Fundraiser Code Has Been Applied.",
            'FUNDRAISER_CODE_ERROR_COUPON': "To participate in the fundraiser, please remove any applied coupons.",
            'FundraiserCodeOK': "Your Fundraiser Code has been applied.",
            'FundraiserValueIsEmpty': "Incomplete field. Please enter the ",
            'Fundraising': "Fundraising",
            'Future': "Future",
            'GetDirections': "Get Directions",
            'GIFT_CARD_REQUIRED': "Gift card number is required.",
            'GIFT_CARD_PIN_REQUIRED': "Pin required.",
            'GiftCard': "Gift Card",
            'GiftCardAlreadyApplied': "This gift card has already been applied to your order.",
            'GiftCardEndingWith': "Gift Card ending with {{last_four}}",
            'GiftCardNumber': "Gift Card Number",
            'GiftCards': "Gift Cards",
            'GO_ErrorDuringGOSave': "An error occured while saving the group order.",
            'GO_ErrorDuringGOSubmit': "An error occured during group order submit.",
            'GO_FinalizeQuestionYesNo': "Finalize the group order and proceed to checkout?",
            'GO_FinalizeWillDeleteWarning': "Finalizing the group order will delete member' orders that have not been submitted.",
            'GO_NotStartedMembers': "member orders not started in this group order belong to:",
            'GO_NotSubmittedMembers': "member orders not submitted in this group order belong to:",
            'GO_OrderFinalization': "<h1 class='warning'> Order finalization</h1><br>Group order is being finalized.<br>Please wait.",
            'GO_OrderUndefined': "The group order is undefined.",
            'GO_UnsubmitOrders3': "Continue submitting the group order will cause unsubmitted members' orders to be deleted and<br>will no longer allow you to add members to the current group order.<br>Are you sure you want to proceed with the checkout?",
            'GoogleMapsErrorAmbiguous': "Google Maps error: Ambiguous addresses at",
            'GoogleMapsErrorResolvingAddress': "Google Maps error: Error while resolving address",
            'GoogleSignInFailed': "Google sign-in was not successful. Please try again or use another sign-in method.",
            'SignInWithGoogle': "Sign in with Google",
            'SignUpWithGoogle': "Sign up with Google",
            'GoToMenu': "Go To Menu",
            'Gratuity': "Gratuity",
            'GROUPORDER': "GROUP ORDER",
            'GroupOrderStatus1': "NOT STARTED",
            'GroupOrderStatus2': "IN PROGRESS",
            'GroupOrderStatus3': "ORDERED",
            'GroupOrderStatus4': "NOT PARTICIPATING",
            'GroupOrderStatus5': "SUBMITTING",
            'GroupOrderStatus6': "CANCELED",
            'GroupOrderTime': "Group Order Time",
            'GRPORD_AddMember': "Add Member",
            'GRPORD_AddNewGroupMember': "Add new group member",
            'GRPORD_AreYouSureCancel': "Are you sure you want to cancel your Group Order?",
            'GRPORD_CancelFailed': "Group Order Cancel failed.",
            'GRPORD_CancelGroupOrder': "Group order has been canceled.",
            'GRPORD_CreateGroupOrder': "Create Group Order",
            'GRPORD_CreateOrder': "Create Group Order",
            'GRPORD_Date': "Date",
            'GRPORD_PayAllDescr': "If Pay for all members is unchecked, each member will be paying for their own meal.",
            'GRPORD_PayAllMembers': "Pay for all members",
            'GRPORD_SelectAll': "Select all group members",
            'GRPORD_SelectMembers': "Select members for your group order",
            'GRPORD_Time': "Time",
            'GRPORD_Welcome': "Invite your friends to join a group order. Each member will get a link to order their meal and have it added to the group.",
            'Guest': "Continue as Guest",
            'HalfHalfPizzaOrderDetail': "?? Half & Half Pizza",
            'HalfAndHalf': "Half & Half",
            'HamburgerMenu': "Hamburger Menu",
            'HaveGiftCard': "Have a gift card?",
            'Hello': "Hello",
            'Hey': "Hey",
            'Hide': "Hide",
            'HIDE_DETAILS': "Hide Details",
            'HHP': "Half & Half Pizza",
            'Home': "Home",
            'Inactive': "Inactive",
            'DeleteMyProfile': "Delete My Profile",
            'IncompleteProfileError': "There is some information missing from your profile. Please complete all the fields to continue.",
            'InProgress': "In Progress",
            'InStore': "In-Store",
            'InStoreLimit': "Oops, this order reached our store's pay in store limit of ?? but you can pay by credit card. We apologize for any inconvenience.",
            'Instructions': "Instructions",
            'ImportDigitalGiftCards': "Import Digital Gift Cards",
            'INVALID_OR_EXPIRED_FUNDRAISER_CODE': "The fundraiser code has expired or is invalid.",
            'INVALID_BIRTHDAY': "Invalid birthday.",
            'InvalidAddr': "Invalid billing address.",
            'InvalidAddress': "Invalid Address",
            'InvalidAddressNoStreetNumber': "The address is invalid or missing a street number. Please review and try again.",
            'InvalidAddressNoZipCode': "The address is invalid or missing a zip code. Please review and try again.",
            'InvalidAge13': "Must acknowledge that you are at least 13 years of age.",
            'InvalidCardNumber': "The gift card number entered is invalid. Please check and try again.",
            'InvalidCarInfo': "Car make, model, and color are required.",
            'InvalidCC': "Invalid credit card number.",
            'InvalidCode': "Invalid Code!",
            'InvalidCommonSize': "Invalid Common Size.",
            'InvalidCouponRequired': "Please enter a promotional code to apply.",
            'InvalidCoupon': "Coupon has expired or is invalid.",
            'InvalidCouponCode': "Invalid coupon code.",
            'InvalidCouponDate': "The coupon is not currently valid or redeemable at this order time.",
            'InvalidCVV': "Invalid CVV(Card verification value)",
            'InvalidDate': "Invalid date.",
            'InvalidDay': "Must select a day.",
            'InvalidEmail': "Invalid email address.",
            'InvalidEmptyEmail': "Email address is required.",
            'InvalidOrExpiredPasscode': "Invalid or expired passcode.",
            'InvalidEmailOrPassword': "Invalid email and/or password. Please enter valid credentials or reset your password",//, or try logging in with your phone number.",
            'InvalidEmailOrPasswordNoPhone': "Invalid email and/or password. Please enter valid credentials or reset your password.",
            'InvalidEmailPhone': "Invalid email address or phone number.",
            'InvalidExp': "Invalid expiration date.",
            'InvalidFirstName': "Invalid first name.",
            'InvalidEmptyFirstName': "First name is required.",
            'InvalidFundraiserCode': "Invalid fundraiser code.",
            'InvalidLastName': "Invalid last name.",
            'InvalidEmptyLastName': "Last name is required.",
            'InvalidMonth': "Please select a month",
            'InvalidOrderTypeCode': "The coupon is not valid for ??.",
            'InvalidPassword': "Password must contain at least one number, one lowercase letter, one uppercase letter, one symbol (* + @ # $ % & !) and a minimum of eight characters.",
            'InvalidPasswordLogin': "Invalid password.",
            'InvalidPasswordMatch': "The passwords do not match.",
            'ConfirmEmailAddress': "Confirm Email Address",
            'EmailsMustMatch': "Emails must match.",
            'ConfirmEmailPasteWarning': "For accuracy, please type your email address instead of pasting it.",
            'InvalidPhone': "Invalid phone number.",
            'InvalidEmptyPhone': "Phone number is required.",
            'InvalidPin': "Invalid PIN. Please enter a valid PIN (1 digit minimum) and try again.",
            'InvalidPromotionCode': "Invalid promotion code.",
            'InvalidReward': "The reward is not currently valid or redeemable at this order time.",
            'InvalidScheduleCode': "The coupon is not valid Today/Now.",
            'InvalidStName': "Invalid billing street name.",
            'InvalidTermsAndPrivacy': "Please agree to the Terms of Use and Privacy Policy before placing your order.",
            'InvalidTimeNotSelected': "Select a deferred time to proceed.",
            'InvalidTimeOutOfRange': "Invalid time. Please select a valid time.",
            'InvalidTip': "Invalid tip amount.",
            'InvalidZip': "Invalid Billing zip code.",
            'InvalidZip1': "Invalid zip code.",
            'InvalidZipCode': "Invalid zip code.",
            'Item': "Item",
            'ItemAdded': "Item Added",
            'ItemComment': "Note: Toppings and/or Coupon Codes added in the box will <strong>NOT</strong> be applied to the item.",
            'Items': "Items",
            'ItemsAddedSuccess': "Item successfully added to your cart!",
            'ItemsAddedToCart_Single': "1 Item Added To Cart",
            'ItemsAddedToCart_Plural': "{{number}} Items Added To Cart",
            'ItemSizeNotHalfHalf': "??? is not available in the selected size. Item size has been updated to ??.",
            'January': "01 - January",
            'JanuaryOnly': "January",
            'Join': "Join",
            'Joined': "Joined",
            'JoinLoyaltyProgramButton': "Join Loyalty Program",
            'JoinLoyaltySignUpConfirmPrivacy': "Privacy Policy",
            'JoinLoyaltySignUpConfirmTerms': "Terms of Use",
            'JoinOurLoyaltyProgram': "Join our loyalty program and earn reward &<br/>exclusive offers.",
            'JoinOurTeam': "Join Our Team",
            'JoinRewards': "Join Rewards",
            'JoinRewardsProgram': "Join Rewards Program",
            'JsonError': "Error.</br>Parsing JSON Request failed!",
            'July': "07 - July",
            'JulyOnly': "July",
            'June': "06 - June",
            'JuneOnly': "June",
            'kilometer': "kilometer",
            'kilometers': "kilometers",
            'kilometer_short': "km",
            'LangAbbr': "EN",
            'LangHeader': "Choose your language",
            'LastName': "Last Name",
            'LastOrderDate': "Last Order Date",
            'LastTransaction': "Last Transaction",
            'Later': "Later",
            'LearnMore': "Learn more",
            'Left': "Left",
            'LifetimePoints': "Lifetime Points",
            'EmailMeUpdatesAboutMyOrder': "Email me updates about my order",
            'TextMeUpdatesAboutMyOrder': "Text me updates about my order",
            'LIFETIME_POINTS_TEMPLATE': "Lifetime {{points_unit}}",
            'LikeToReceiveOrderStatusNotificationsViaEmail': "I would like to receive order status notifications via email:",
            'LikeToReceiveOrderStatusNotificationsViaEmailAndText': "I would like to receive order status notifications via email and text messages:",
            'LikeToReceiveOrderStatusNotificationsViaText': "I would like to receive order status notifications via text messages:",
            'LIMITHOUS': "Limited hours",
            'Loading': "Loading...",
            'LocationNotFound': "Location not found. Try a city or zip code.",
            'Locations': "Locations",
            'Locations_Heart_NoName_Error': "You must insert a store name. Please try again.",
            'Locations_Heart_SaveLocationName_NotSignedIn': "To create a favorite store you need to Sign In or Create a Profile.</br>Would you like to get directed to the Sign In page?",
            'Locations_Heart_SaveLocationName_RemoveHeart': "Are you sure you want to remove the favorite store?",
            'Locations_Heart_SaveLocationName_SignedIn': "Would you like to give this store a name?",
            'Locations_Search': "Search: City, State, or Zip Code...",
            'LocationServicesBlocked': "Your location services are blocked. For the store to find your location quickly, please grant location access.",
            'LocationSelector': "Location Selector",
            'LogIn': "Log In",
            'Login': "Login",
            'LogonError': "Unknown logon error happened. Please retype your email address and password then try to login again.",
            'LogOut': "Log Out",
            'LoyaltyAvailablePoints': "{{points}} Available Points",
            'LoyaltyCantReachInfoError': "Oops! We can't reach your loyalty info, please try again later.",
            'LoyaltyCarrierMsg': "*carrier text messaging charges may apply",
            'LoyaltyCreateWelcome': "This is our new loyalty program!",
            'LoyaltyRewardsCannotCombineWithBundledOffers': "Loyalty rewards or offers cannot be combined with bundled offers.",
            'LoyaltyMemberSince': "Loyalty Member Since",
            'LOYALTY_POST_ORDER_FAILED': "Your order was placed successfully! Your loyalty points are still being processed and will be added to your account shortly.",
            'LoyaltyServerOffline': "Oops! Something is not working. Please try again later.<br>If the issue persists, please proceed as a guest and place your order.",
            'LoyaltyStoreMsg': "If you like to join ??? located at ???? loyalty program, please enter your cell phone number below",
            'LoyaltyVerifyMsg': "A verification code will be send to your phone",
            'MakeItAGroupOrder': "Make It a Group Order",
            'Makeithalfhalfpizza': "Make it half & half pizza",
            'MakeItHalfAndHalf': "Make It Half & Half",
            'MakeMyDefaultStore': "Make My Default Store",
            'March': "03 - March",
            'MarchOnly': "March",
            'MarkAttributesDidNotSaved': "Loyalty program attributes have not been saved. To update them please use Customer Profile feature.",
            'MarkEmail': "Email",
            'MarkInfo': "I would like to receive offers and product information via:",
            'MarkSelDay': "Select a day",
            'MarkSelMonth': "Select a month",
            'MarkText': "Text",
            'MarketingEmail': "Keep me updated with exclusive offers via Email",
            'MarketingText': "Keep me updated with exclusive offers via Text",
            'MarketingSurvey': "Subscribe to order survey emails for a chance to earn additional discounts.",
            'May': "05 - May",
            'MayOnly': "May",
            'MeetPromiseTimeError': "We cannot meet the order promise time. Please call store @ %storePhoneNumber%",
            'MeetPromiseTimeWarning': "The store will stop accepting online ?? orders in 5 minutes. Please finish your order within 5 minutes.",
            'Menu': "Menu",
            'MenuItems': "Menu Items",
            'MfaChallengeExpired': "Your verification has expired. Please sign in again.",
            'MfaChallengeExpiredAccount': "Your verification has expired. Please try again.",
            'MfaCodeInvalid': "That code isn't right. Try again.",
            'MfaCodeLabel': "Verification code",
            'MfaCodePlaceholder': "6-digit code",
            'MfaCodeResent': "A new code has been sent. Valid for 15 minutes.",
            'MfaCodeSubtitle': "MFA sent &middot; valid for 15 minutes",
            'MfaCodeTitle': "Enter your code",
            'MfaSentTo': "Sent to {0}",
            'MfaTooManyAttempts': "Too many verification attempts. Please try again later.",
            'MfaTrustBrowser': "Trust this browser for 30 days",
            'MfaVerify': "Verify",
            'MfaVerifyAndSignIn': "Verify and sign in",
            'MfaVerifySubtitle': "For your security, confirm your email or phone number to continue signing in.",
            'MfaVerifySubtitleAccount': "For your security, confirm your email or phone number to continue.",
            'MfaVerifyTitle': "Verify it's you",
            'MX': "Mexico (+52)",
            'mile_short': "mi",
            'mile': "mile",
            'miles': "miles",
            'MinDelAmt': "This purchase is below the minimum order amount for delivery of $??. Please try again.",
            'REQUIRED_MODIFIER_GROUP_MINIMUM': "You must select at least {amount} option(s) from {display_group}",
            'Minutes': "Minutes",
            'Minutes_Number': "{{minutes}} Minutes",
            'MISSING': "Missing information!<br/>Please complete before placing your order.",
            'ModifierLeftSide': "{{modifier_name}} Left Side",
            'ModifierPremodifierLeftSide': "{{modifier_name}} Premodifier Selection Left Side",
            'ModifierPremodifierRightSide': "{{modifier_name}} Premodifier Selection Right Side",
            'ModifierRightSide': "{{modifier_name}} Right Side",
            'ModifierIsNotCompatible': "??? is not offered on selected half.",
            'Monday': "Monday",
            'Month': "Month",
            'MultipleMemberOrderWindowAlert': "Another session for this member order has been opened. This window will be redirected to Store home page.",
            'MyAvailableGiftCards': "My available gift cards",
            'MYGROUPORDER': "MY GROUP ORDER",
            'MyAccount': "My Account",
            'MyAccountPage': "My Account page",
            'MyCredits': "My Credits",
            'MyHistory': "My History",
            'MyHistory_FirstTimeMessage': "All your history will be available here. Start accumulating rewards today!",
            'MyMarcosRewards': "My Marco's Rewards",
            'MyMembership': "My Membership",
            'MyOffers': "My Offers",
            'MYORDER': "MY ORDER",
            'MyOrders': "My Orders",
            'MyOrders2_FavoriteIconClick': "Name your favorite order",
            'MyOrders2_FetchOrderData_Error': "There was an error retrieving your orders. Please try again.",
            'MyOrders2_FetchOrderDetails_Error': "There was an error retrieving your order details. Please try again.",
            'MyOrders2_ToggleFavoriteOrder_Error': "There was an error saving your favorite order changes. Please try again.",
            'MyOrders_ReOrder_AllError': "These items are not currently available. You may select other items instead.",
            'MyOrders_ReOrder_MultipleError': "are currently unavailable. You may select another item instead.",
            'MyOrders_ReOrder_SingleError': "is currently unavailable. You may select another item instead.",
            'MyPoints&Rewards': "My Points & Rewards",
            'MyPoints': "My Points",
            'MyProfile': "My Profile",
            'MyRewards': "My Rewards",
            'MyRewardsAndOffers': "My Rewards & Offers",
            'MyRewardsSlogan': "<div>My</div><div>Rewards</div>",
            'Name': "Name",
            'NameYourItem': "Name Your Item",
            'Nearby': "Nearby",
            'NearbyRestaurants': "Nearby Restaurants",
            'NewAddress': "New Address",
            'NewDelAddr': "Enter New Delivery Address.",
            'NEW_ADDRESS_UPDATED_SUCCESSFULLY': "New {} address updated successfully.",
            'NewPassword': "Enter New Password",
            'Next': "Next",
            'No': "No",
            'NoAddressesAddedToAccount': "There are no addresses added to your account",
            'NoAvailableGiftCards': "You have no available gift cards.",
            'NoAvailableRewardsOrOffers': "You have no available rewards or offers.",
            'NoCardsAddedToAccount': "There are no cards added to your account",
            'NoCreditHistory': "No credit history on record.",
            'NoDeliveryAddress': "No delivery address has been selected.",
            'NoDiscountAssociatedWithCode': "No discount associated with this code.",
            'NoFavoriteItems': "You haven't added any Favorite Items yet!",
            'NoFutureOnlineForSelectedDate': "Deferred orders are currently unavailable for the selected date.<br>Please choose a different date or contact the store for assistance.",
            'NoFutureOnlineOrder': "Store is not accepting deferred online orders on the selected date.",
            'NoImNot': "No, I am not.",
            'NoItemAdded': "No Item has been added to order.",
            'NoItemsAvailable': "None of the items in this order are currently available.",
            'NoMenuItemsHaveBeenSelected': "No Menu Items Have Been Selected",
            'None': "None",
            'NO_OFFERS': "No Offers",
            'NoOffers': "No offers yet.",
            'NoOnlineNow': "Store in not accepting online orders now. however you may place an order for later today. ",
            'NoOtherStore': "No (Select another location)",
            'NoPayment': "No payment has been selected.",
            'NoPreferredDeliveryAddress': "No Preferred Delivery Address.",
            'NoPreferredCarryOutStore': "No Preferred Carry Out Store.",
            'NoRecentOrders': "You have no recent orders.",
            'NoRedeemItemGroups': "No rewards available to redeem yet. Keep earning points with each order!",
            'NoRewards': "You have no rewards to show.",
            'NoRewardsActivity_Single': "No rewards activity in the last day",
            'NoRewardsActivity_Plural': "No rewards activity in last {{number}} days",
            'NoRewardsAvailableInGroup': "No rewards currently available in this group.",
            'NoSavedPaymentMethods': "No Saved Payment Methods.",
            'NOTAMEMBER': "NOT A MEMBER?",
            'NotEnrolled': "Not enrolled in loyalty program",
            'NoThanks': "No Thanks",
            'Notifications': "Notifications",
            'notifsAnd': " and ",
            'notifsConsent': "I would like to receive order status notifications via ",
            'notifsEmail': "email",
            'notifsText': "text",
            'NoTime': "Unfortunately there is not enough time to make an online ??? order.",
            'NoTip': "No Tip",
            'NotOfferedInSelStore': "?? is not being offered in the selected store.",
            'November': "11 - November",
            'NovemberOnly': "November",
            'Now': "Now",
            'Number': "Number",
            'Nutrition': "Nutrition",
            'October': "10 - October",
            'OctoberOnly': "October",
            'off': "off",
            'OFF': "OFF",
            'Offers': "Offers",
            'Ok': "Ok",
            'OK': "Ok",
            'OldPassword': "Old Password",
            'OLO_General_Error': "<h1 class='warning'>Oops!</h1>It looks like something went wrong. Please try again!<br /><br />If the issue persists, we invite you to call the restaurant at %storePhoneNumber% and place your order over the phone.<br /><br />We sincerely apologize for the inconvenience.<br /><span style='color:#dddddd; font-size:8px;'>noconnt</span>",
            'OnePrimAddr': "Please select one primary address. ",
            'ONLINE_ORDERS_UNAVAILABLE': "The store is not accepting online orders at this time.",
            'OnlyOneDiscountAllowedAtATimeError': "Only one discount is allowed at a time.<br>Please remove the existing discount to continue.",
            'OpenOrders': "Open Orders",
            'OpensAt': "Opens at {time}",
            'OpenUntil': "Open until {time}",
            'Optional': "Optional",
            'or': "or",
            'Or': "Or",
            'Order': "Order",
            'OrderCart': "Order Cart",
            'OrderCenter': "Order Center",
            'OrderComment': "Note: Coupons added in the box will <strong>NOT</strong> be applied to your order.",
            'OrderDateTime': "Order Date & Time",
            'OrderDetailItemName': "?? ???",
            'OrderDetails': "Order Details",
            'ORDER_DISPATCHED_THIRDPARTY': "Your order will be dispatched using {{provider_name}}",
            'OrderIsClosed': "Order is closed.",
            'OrderNow': "Order Now",
            'OrderNumber': "Order Number",
            'OrderPlaced': "Order Placed",
            "Orders": "Orders",
            'OrderSummary': "Order Summary",
            'OrderConfirmation': "Order Confirmation",
            'OrderTime': "Order Time",
            'OrderTotal': "Order total",
            'OrderType': "Order Type",
            'OrderTypeNotOfferedAtThisLocation': "{{order_type}} is not offered at this location",
            'Other': "Other",
            'OtherReason': "The \"Other\" reason is required.",
            'OUT_DELIVERY': "This address has been marked as do not deliver address:<br>{{address}}",
            'OutTimeDelivery': "The address is in a zone outside of store's delivery hours.",
            'PaidAmount': "Paid Amount",
            'PaidWithSavedCreditCard': "Paid with saved credit card",
            'PartialUpdateInfo': "Partial info was successfully updated.",
            'PARTICIPATING_IN_FUNDRAISER': "You are participating in {{fundraiser_name}}",
            'pass6Char': "Your password must be at least 6 characters.",
            'passDigit': "Your password must contain at least one digit.",
            'passLetterLower': "Your password must contain at least one lowercase letter.",
            'passLetterUpper': "Your password must contain at least one uppercase letter.",
            'Passcode': "Passcode",
            'Password': "Password",
            'PasswordExpiredSentNewEmail': "Your password has expired. We have sent you an email to reset your password.",
            'PasswordNoMatch': "The passwords you entered do not match.",
            'PasswordReset': "We've sent a message to {email}. Please check your spam folder if you haven't received the email in 5 minutes.",
            'PasswordSaveError': "There was an error updating your password. Please try again.",
            'PasswordUpdatedSuccessfully': "Your password has been updated successfully.",
            'PasswordResetSuccessfully': "Your password has been successfully reset.",
            'PayInStore': "Pay In Store",
            'Payment': "Payment",
            'PaymentMethod': "Payment Method",
            'PaymentMethods': "Payment Methods",
            'PaymentTypeNotApplicableForFutureDate': "Selected payment type is not applicable for future date orders.",
            'PayWithDollars': "Pay With Dollars",
            'Phone': "Phone",
            'PhoneCodeIsInvalidOrExpired': "The code is either invalid or expired.<br>Please try again.",
            'PhoneCountryCode': "Phone Country Code",
            'PhoneFormat': "(XXX) XXX-XXXX",
            'PhoneNoMatch': "The phone numbers you entered do not match.",
            'PHONE_ALREADY_EXISTS': "Phone number already exists.",
            'PickUp': "Pick Up",
            'PickUpFrom': "Pick Up From",
            'PickUpTime': "Pick Up Time",
            'Pin': "Pin",
            'PlaceAnOrderToSeeYourNextMembership': "Place an order to see your membership",
            'PlaceAnOrderToSeeYourNextReward': "Place an order to start earning rewards",
            'PlaceOrder': "Place Order",
            'PleaseEnterAmountGreaterThan': "Please enter an amount greater than {{x}}",
            'PleaseEnterAmountLessThanAmountDue': "Please enter an amount less the the amount due of {{x}}",
            'PleaseEnterAmountWithinRangeFrom': "Please enter an amount within the range from {{x}} to {{y}}",
            'PleaseSelectItems': "Please Select Items",
            'point': "point",
            'points': "points",
            'Points': "Points",
            'POINTS_LABEL_WITH_POINTS': "{{points}} {{points_unit}}",
            'PointsAvailable': "Points Available",
            'PointsAwayFromNext': "points away from your next",
            'PointsEarned': "Loyalty points earned",
            'PointsLastUpdated': "Points Last Updated",
            'PointsRedeemed': "Points Redeemed",
            'PointsRemain': "Points Remain",
            'PointsRequired': "Points Required: {{points}}",
            'POINTS_REQUIRED_LABEL': "{{points_unit}} required: {{points}}",
            'PointsTo': "Points to ",
            'PointsToNextReward': "points to next reward",
            'PointsToTheNextTier': "Points to the next tier",
            'PoweredBy': "Powered by",
            'PreferredCarryOutStore': "Preferred Carry Out Store",
            'PreferredCarryOutStoreUpdateSuccess': "Your preferred carryout store has been successfully updated.",
            'PreferredCarryOutStoreUpdateFailure': "Unable to update your preferred carryout store.",
            'PreferredDeliveryAddress': "Preferred Delivery Address",
            'PreModsNotSet': "Premodifiers have not been set.",
            'PR': "Puerto Rico (+1)",
            'Price': "Price",
            'Primary': "Primary",
            'privacy': "privacy policy",
            'PrivacyPolicy': "Privacy Policy",
            'PrivacyTerms': "I have read and agree to the ",
            'ProceedToCartToCheckout': "Proceed to cart to checkout",
            'Profile': "Profile",
            'ProfileWithPhoneDoesntExist': "There was a problem retrieving the phone number. Please try again or use a different number.",
            'Promise': "Promise",
            'PromiseTime': "Promise Time",
            'PromosRewards': "Promos & Rewards",
            'PromotionHasBeenRedeemed': "Promotion has been redeemed.",
            'PromotionHasExpired': "Promotion has expired.",
            'Promotions': "Promotions",
            'PromotionCode': "Promotion Code",
            'pts': "pts",
            'ptsEarned': "pts earned",
            'Quantity': "Quantity",
            'QuantityAndAddToOrder': "Quantity and add to order",
            'ReadAndAcceptTermsOfUseAndPrivacyPolicy': "I have read and accept the {{terms_of_use}} and {{privacy_policy}}.",
            'READY_FOR_PICKUP_BY': "Ready for Pick Up By",
            'READY_FOR_CURBSIDE_PICKUP_BY': "Ready for Curbside Pick Up By",
            'RecentOrderHeading': "{0} order, {1}",
            'RecentOrders': "Recent Orders",
            'RecentlyOrdered': "Recently Ordered",
            'RecentOrder_NotAllModifiersAvailable': "The item(s) {{itemNames}} have been removed from the order as the exact modifications from the previous order are no longer available.",
            'Redeem': "Redeem",
            'RedeemBy': "Redeem By",
            'RedeemableItems': "Redeemable Items",
            'Redeemed': "Redeemed",
            'RedeemFor': "Redeem for",
            'RedeemWithPoints': "Redeem With Points",
            'REDEEM_WITH_POINTS_TEMPLATE': "Redeem With {{points_unit}}",
            'RedeemCurrency': "Redeem Rewards",
            'RedeemPoints': "Redeem Points",
            'REDEEM_POINTS_TEMPLATE': "Redeem {{points_unit}}",
            'RedeemRewards': "Redeem Rewards",
            'RedeemRewardHasAlreadyBeenAppliedError': "A redeem reward has already been applied.<br>Please remove the already applied redeem reward.",
            'ReEmail': "Re-enter Email",
            'ReEnterExisting': "Please Re-Enter your existing password",
            'REJECT_ALL': "REJECT ALL",
            'RemainingAmount': "Remaining Amount:",
            'RemainingBalance': "Remaining Balance",
            'RememberMe': "Remember Me",
            'RememberMe30Days': "Remember Me for 30 Days",
            'UseBiometricLogIn': "Use Biometric Log In",
            'Remove': "Remove",
            'RemoveCoupon': "Remove Coupon",
            'RemoveFromFavorites': "Remove from Favorites",
            'RemoveGiftCard': "Remove Gift Card",
            'RemoveHalf': "Remove Half",
            'Reorder': "Reorder",
            'RePassword': "Re-enter Password",
            'RepeatPassword': "Repeat Password",
            'RePhone': "Re-enter Phone",
            'RequestResetPassword': "Request password reset",
            'Required': "Required",
            'REQUIRED_BIRTHDAY': "Birthday is required.",
            'RequiredField': "Required Field.",
            'RequiredMonth': "Month is required.",
            'RequiredDay': "Day is required.",
            'RequiredDefaultStore': "Your store must be selected.",
            'ResendCode': "Resend Code",
            'ResetPass_SessionExpired_Error': "Your password reset session has expired. Try to reset your password again.",
            'ResetPass_Unexpected_Error': "Unexpected error happened while trying to reset your password. Please try again later.<br>Error: ",
            'ResetPassword': "Reset Password",
            'ResetPasswordSuccessful': "Your password was successfully reset.",
            'RESUBSCRIBE': "Re-subscribe",
            'REVIEW_ORDER': "Review Order",
            'Rewards': "Rewards",
            'RewardsActivity': "Rewards Activity",
            'RewardsFAQ': "Rewards FAQ",
            'RewardsGreeting': "Hey, {{first_name}}!",
            'RewardsKeepUnlocking': "You're earning rewards with every bite! Keep going to unlock more delicious deals and benefits.",
            'RewardGroupItems': "Reward Group Items",
            'RewardGroupItemsHeader': "{{ reward_group_name }} selection",
            'Right': "Right",
            'RoundUp': "Round Up",
            'ROUTE_MULTI_STOPS': "The driver's route may include multiple stops along the way",
            'ROUTE_UNAVAILABLE': "Delivery route is currently unavailable.",
            'Saturday': "Saturday",
            'Save': "Save",
            'Save_your_profile_changes': "Save your profile changes?",
            'SaveCreditCardForFutureOrders': "I would like to have my credit card information saved for future orders.",
            'SavedAddresses': "Saved Addresses",
            'SavedCards': "Saved Cards",
            'SavingPassword': "Saving Password",
            'Savings': "Savings",
            'SaveNewAddress': "Save New Address",
            'ScheduleForLater': "Schedule For Later",
            'Search': "Search",
            'SearchCityStateOrZipCode': "Search City, State or Zip Code...",
            'SearchingCities': "Searching cities..",
            'SearchingZipCodes': "Searching Zip codes..",
            'SecondHalf': "Second Half",
            'SecurityCode': "Security Code:",
            'SecurityCodeCVV': "Security Code (CVV)",
            'Select': "Select",
            'SelectAnotherLocation': "Select another location",
            'SelectOrderType': "Select order type",
            'SelectAvailableTimeSlot': "Select a time from the available time slots:",
            'SelectDeliveryAddress': "Select Delivery Address",
            'SelectItemsToAddtoOrder_Single': "Select 1 item to add to your order",
            'SelectItemsToAddtoOrder_Plural': "Select {{number}} items to add to your order",
            'SELECTED': "Selected",
            'SELECTED_MODIFIERS': "Selected Modifiers",
            'SelectedStore': "Selected Store",
            'SelectedTime': "Selected Time",
            'SelectedTimeNotAvailableSelectNew': "The selected time slot is full.<br>Please pick another available time.",
            'SelectedTimeReserveFailed': "We couldn't reserve that time slot.<br>Please try again or pick another time.",
            'SelectEvent(s)': "Select Event(s)",
            'SelectItem': "Select Item",
            'SelectLocation': "Select<br>Location",
            'SelectStore': "Select Store",
            'SelectAddress': "Select Address",
            'ASSIGN_POINTS_SELECT_MEMBERS': "Select Member(s) to Assign Points for This Challenge.",
            'SelectMembers(s)': "Select Member(s)",
            'SelectPaymentMethod': "Select Payment Method",
            'SelectTime': "Select Time",
            'SelectWhenYouWantToPlaceOrder': "Please select when you want your order.",
            'SelectSecondHalf': "Select Second Half",
            'SelectYourOrderType': "Select Your Order Type",
            'SelModNoSize': "{modName} is not offered on {sizeName} {itemName}.",
            'SelModNoSize1': "Selected ?? is only being offered on ???. <br/>Please change the size to ???? and try again.",
            'SelModNoSize2': "Selected ?? is not being offered on ???. ???? is available on following sizes ?????.",
            'SelSizNoMod': "Selected size is not valid for ",
            'SelTop': "Please select a topping.",
            'SendingOrder': "Sending Order...",
            'September': "09 - September",
            'SeptemberOnly': "September",
            'SetAsDefault': "Set as Default",
            'Settings': "Settings",
            'ShowEarlierOrders': "Show Earlier Orders",
            'Show': "Show",
            'ShowMore': "Show More",
            'SignIn': "Sign In",
            'SignInSlogan': "Start earning {{points_unit}} to <span class='highlight'>redeem</span> towards <wbr/> your <span class='highlight'>favorite</span> menu items",
            'SignInToSeeYourFavorites': "Sign in to see your favorites.",
            'SignInWithApple': "Sign In With Apple",
            'SignInWithFacebook': "Sign In With Facebook",
            'SignInScreen': "Sign In Screen",
            'SignInTextPrivacyTerms': "By selecting Sign In, you agree to receive a passcode via text and agree to our ",
            'SignInToUseRewards': "Sign in to use rewards",
            'SignOut': "Sign Out",
            'SignOut_AreYouSure': "Are you sure you want to sign out?",
            'SignOut_AreYouSure_Empty_EmptyCart': "Are you sure you want to sign out?",
            'SignOut_AreYouSure_EmptyCart': "Are you sure you want to sign out, your basket will be emptied?",
            'SignUp': "Sign Up",
            'SignUp2': "Sign Up",
            'SignUpScreen': "Sign Up Screen",
            'SignUpTerms': "By checking this box, you acknowledge the receipt of, review of, and agreement to the terms of use and privacy policy.",
            'SingleUseCoupon': "Single Use Coupon",
            'SIZE': "Size",
            'Size': "Size",
            'Skip': "Skip",
            'Slice': "Slice",
            'SmsDisclaimer': "By providing your phone number, you consent to receive automated text messages from {0} regarding your account, updates, and promotions. Msg & data rates may apply. Frequency varies. Reply STOP to opt out or HELP for assistance.",
            'SmsDisclaimerTerms': "Terms",
            'SmsDisclaimerViewPrivacy': "View our {0}.",
            'SmsDisclaimerViewTerms': "View our {0}.",
            'SmsDisclaimerViewTermsAndPrivacy': "View our {0} and {1}.",
            'SomethingWentWrong': "Something went wrong.",
            'spChar': "Your password must contain at least one of<br/>these special characters ",
            'SPECIAL_INSTRUCTIONS': "Special Instructions",
            'SpecialInstructions': "Special Instructions",
            'SameAsDel': "Same As Delivery Address",
            'StartOrder': "Start Order",
            'StartOrderWithOrderType': "Start {{order_type}} Order",
            'StartingBalance': "Starting Balance",
            'State': "State",
            'StoreAddress': "Store Address",
            'STORE_CLOSED': "The store is currently closed.",
            'STORE_CLOSED_OPENS_AT': "The store is currently closed and will reopen at {{time}}.",
            'StoreClosedOn': "The store is closed on ??.",
            'STORE_CLOSED_HOLIDAY': "The store is closed on {{holiday_name}}. Please select a different date.",
            'STORE_CLOSING_SOON': "The store is still open but will close in {{minutes}} minutes, which is not enough time to place an order.",
            'ONLINE_HOURS_ENDING_SOON': "The store will stop accepting online {{order_type}} orders in {{minutes}} minutes, which is not enough time to place an order.",
            'StoreDistanceAway': "{distance} {type}",
            'StoreDistanceDialogMessage': "You're placing a {{order_type}} order at:<br>{{store_address}}.<br>The selected store is {{store_distance}} away from your current location!<br>Are you sure you want to proceed with this order?",
            'StoreDistanceDialogMessage_StoreSelector': "{{store_address}}.<br>The store you selected is {{store_distance}} away from your current location!<br>Are you sure you want to proceed with this order?",
            'StoreDoesNotAcceptDeferredOnlineOrdersOn': "The store does not accept deferred online orders on ??.",
            'StoreDoesNotAcceptOnlineOrdersOn': "The store does not accept online orders on ??.</br>Please call the store at ??? to place your order over the phone.",
            'StoreHours': "Store Hours",
            'storeInfoName': "Ordering From:",
            'StoreIsAlreadyClosed': "The store is already notified",
            'StoreLocatorDesc': "Location services are disabled. To enable, exit this screen, activate location services, and retry. If not, choose your country and enter your zip code.",
            'storeNotFound': "There are no stores within 25 miles of your Zip Code (%zipcode%).Please, try a new Zip Code in the Search Field,. Otherwise, please enable location services.",
            'StorePhone': "Store Phone",
            'StreetAddress': "Street Address",
            'StreetAddressPlaceholder': 'Enter Your Address',
            'StreetName': 'Street Name',
            'StreetNo': 'Street Number',
            'SUBMIT': "SUBMIT",
            'SubmitOrder': "Place your order",
            'SUBSCRIPTION_CONFIRMED': "Your subscription has been successfully confirmed.<br>You've been added to our list and will hear from us shortly.",
            'SubSurv': "Subscribe to Order Survey emails",
            'Subtotal': "Subtotal",
            'SuccessfullyEnrolled': "You have successfully enrolled in the loyalty program.",
            'SuccessfullyJoinedLoyaltyProgram_MsgBoxOK': "You have successfully joined our Loyalty program!",
            'SuccessfullyUpdatedProfilePage_MsgBoxOK': "Your profile has been updated successfully.",
            'SuccessfullyUpdatedProfilePageJoinedLoyalty_MsgBoxOK': "Your profile has been updated successfully.</br>You have successfully joined our Loyalty program!",
            'SuccessUpdateInfo': "Successfully updated info.",
            'Sunday': "Sunday",
            'SwapHalf': "Swap Half",
            'SystemCannotLocateAddress': "System is not able to locate the address.",
            'Table': "Table",
            'TakeOut': "Take Out",
            'Tax': "Tax",
            'TAXES': "Taxes",
            'TemporarilyClosed': "Temporarily Closed",
            'terms': "terms of use",
            'TermsOfUse': "Terms of Use",
            'TestBrand': "Welcome to {{brand}}",
            'THANK_YOU': "Thank you",
            'ThankYouFirstName': "Thank You, {{first_name}}",
            'Thursday': "Thursday",
            'tier': "tier",
            'Time': "Time",
            'TimeFrameToExpireContinue': "Your reserved time slot is about to expire.<br>Would you like to extend your time?",
            'Timeout': "Request timeout!",
            'Tip': "Tip",
            'ToBePaidByCreditCardUponDelivery': "To be paid by credit card upon delivery",
            'ToBePaidInCash': "To be paid in cash",
            'ToChange': "Change",
            'TodayHours': "Today",
            'TogoSupplies': "To-Go Utensils",
            'tomorrow': "tomorrow",
            'Toppings': "Toppings",
            'Total': "Total",
            'Tracker_FetchTrackerData_Error': "There was an error retrieving your order. Please try again.",
            'Tracker_IncorrectAccount_Error': "This order does not belong to this account. Please sign in with the correct account.",
            'Tracker_Status_Error': "Your order preparation progress will be available starting from ",
            'TrackMyOrder': "Track My Order",
            'TrackOrder': "Track Order",
            'TrackOrder_FetchTrackOrderData_Error': "Unable to load order details. Please try again later.",
            'TrackYourOrder': "Track Your Order",
            'Transaction': "Transaction",
            'TransactionCarriedOutThrough': "Transactions carried out through:",
            'TransactionNumber': "Transaction Number",
            'TransactionNumberAbr': "Transaction No.",
            'Tuesday': "Tuesday",
            'UnableAutoSignIn': "Unable to Automatically Sign In. Please Sign In Manually.",
            'UnableCreateAccount': "Unable to create account.",
            'UnableEnrolled': "Unable to enroll in loyalty program..",
            'UnableEditProfile': "Please use your Loyalty Application to update the profile. We are working to enable editing here. We apologize for any inconvenience this may cause.",
            'UnableLogin': "Unable to login. Please try again.",
            'UnableResetPassword': "Unable to reset your password.<br/>If this is your first time visiting our new online ordering, continue as guest and create a new user profile at the end of this order.",
            'UnableToDelete': "Unable to delete your profile.",
            'UnableToRetrieveCampaignCoupon': "This code is invalid or has already been redeemed.",
            'UnableToRetrieveReward': "Unable to retrieve the reward. We apologize for the error.<br>Please try again later.",
            'UnableToRetrieveSurveyCoupon': "Unable to retrieve the survey coupon. We apologize for the error.<br>Please try again later.",
            'UnableVerifyPhone': "Unable to verify your phone number.",
            'Unavailable': "Unavailable",
            'UnavailableSize': "Unavailable Size",
            'UnavailableItemModifications1': "The following item(s) have been removed from the order as the exact modifications from the previous order are no longer available:",
            'UnavailableItemModifications2': "The items are still available to order, but the modifications will need to be reselected.",
            'UnexpectedError': "An unexpected error occured.",
            'UnexpectedError500': "An unexpected error occurred.</br>Please try again later.",
            'US': "United States (+1)",
            'UnknownAddressError': "Invalid address or the address is out of store's delivery area.",
            'UnknownError': "Unknown Error!",
            'UNSUBSCRIBE': "Unsubscribe",
            'UNSUBSCRIBE_CONFIRM_BUTTON': "Yes, unsubscribe me",
            'UNSUBSCRIBE_REASON_PROMPT': "If you have a moment, please let us know the reason for your unsubscription (optional):",
            'UNSUBSCRIBED': "Unsubscribed",
            'UNSUBSCRIBED_BY_MISTAKE_QUESTION': "Did you unsubscribe by mistake?",
            'UNSUBSCRIBED_NOTICE': "This address has been unsubscribed and will no longer receive marketing emails.",
            'UNSUBSCRIBED_REMOVED_CONFIRMATION': "You have been successfully removed from our subscriber list and will no longer receive campaign emails.",
            'UNSUBSCRIBED_REOPTIN_PROMPT': "If this unsubscription was made in error, you can opt back in:",
            'Update': "Update",
            'NotNow': "Not Now",
            'UpdateMyProfile': "Update My Profile",
            'UPGRADE': "UPGRADE",
            'UppercaseOr': "OR",
            'ValidOnlineOnly': "Valid Online Only",
            'VIEW': "View",
            'ViewAll': "View All",
            'ViewAllSelected': "View All Selected",
            'ViewCurrentPromotions': "View Current Promotions",
            'VIEW_DETAILS': "View Details",
            'ViewOrderDetails': "View Order Details",
            'Void': "Void",
            'Wallet': "Wallet",
            'Wednesday': "Wednesday",
            'WELCOME': "Welcome to",
            'welcome': "Welcome",
            'WelcomeTo': "Welcome to",
            'WeArePreparingOrder': "We're preparing your order",
            'WeReceivedOrder': "We have received your order",
            'Whole': "Whole",
            'XtraMiles': "The selected store is ?? miles from your current location!<br/>Are you sure you want to proceed with this order?",
            'XtraKilometers': "The selected store is ?? kilometers from your current location!<br/>Are you sure you want to proceed with this order?",
            'Year': "Year",
            'Yes': "Yes",
            'YesDeleteMyProfile': "Yes, Delete My Profile",
            'YesIm': "Yes, I am.",
            'YesProceed': "Yes (Proceed with order)",
            'YouAreNotEnrolled': "You are not enrolled in loyalty program.",
            'YouAreNotSignedIn': "You are not signed in!",
            'YouAreParticipatingIn': "You are participating in",
            'YouCanTrackYourOrderWhichWillBeDeliveredInAboutMinutes': "You can track your order, which will be delivered in about {{minutes}} minutes.",
            'YouCanTrackYourOrderWhichWillBeDeliveredAt': "You can track your order, which will be delivered at {{dateAndTime}}.",
            'YouCanTrackYourOrderWhichWillBeReadyInAboutMinutes': "You can track your order, which will be ready in about {{minutes}} minutes.",
            'YouCanTrackYourOrderWhichWillBeReadyAt': "You can track your order, which will be ready at {{dateAndTime}}.",
            'YouDontHaveAnyItemsInYourOrder': "You Don't Have Any Items In Your Order",
            'YoullReceiveEmailConfirmation': "You'll receive an email confirmation at {{email}}.",
            'YouMightAlsoLike': "You Might Also Like...",
            'YourInformation': "Your Information",
            'YourItems': "Your Items ({{number}})",
            'YourOrderIsScheduledFor': "Your order is scheduled for {{datetime}}",
            'YourOrderWillBeDeliveredInAboutMinutes': "Your order will be delivered in about {{minutes}} minutes.",
            'YourOrderWillBeDeliveredAt': "Your order will be delivered at {{dateAndTime}}.",
            'YourOrderWillBeReadyInAboutMinutes': "Your order will be ready in about {{minutes}} minutes.",
            'YourOrderWillBeReadyAt': "Your order will be ready at {{dateAndTime}}.",
            'Youre': "You're",
            'YOUR_POINTS_AWAY_FROM_REWARD': "You're <b>{{points}} {{points_unit}}</b> away from your next <b>{{reward_name}}</b>",
            'YourOrderDelivered': "Your order has been delivered",
            'YourProfileDeleted': "Your profile deleted successfully",
            'YOUR_OFFERS': "Your Offers",
            'YOUR_CREDITS': "Your Credits", 
            'YourOrderOutForDelivery': "Your order is out for delivery",
            'YourOrderReadyForPickup': "Your order is ready for pickup",
            'YourStore': "Your Store",
            'YouveGot': "You've got",
            'ZENDLOGIN': "Unable to log you in.</br>If this is your first time visiting our new online ordering, please complete all the fields (including the password) to create a new user profile.",
            'ZIP': "ZIP",
            'Zip': 'Zip',
            'ZipCode': "ZIP Code",
            'ZipCodeRequired': "Zip Code required.",
            'ZWELCOME': "Welcome,</br>We have upgraded our online ordering system to provide faster more secure service for you. This requires that you create a new user profile. At the end of this order please complete the requested information and we will be sure your profile is securely updated. Thank you for being a loyal customer!!",
        },
        'es-MX': {
            '1stHalf': "1da Mitad",
            '2ndHalf': "2da Mitad",
            '400': "Servidor ha entendido la petición, pero el contenido de la solicitud no era válida.",
            '401': "El acceso no autorizado.",
            '403': "Prohibida recurso no se puede acceder.",
            '500': "Error Interno del Servidor.",
            '503': "Servicio No Disponible.",
            'Abort': "La solicitud ha sido abortada por el servidor!",
            'ACCEPT_ALL': "ACEPTAR TODO",
            'Account': "Cuenta",
            'AccountDeleteSuccess': "Tu cuenta ha sido eliminada exitosamente.",
            'AccountDeleteFailure': "No se pudo eliminar tu cuenta.",
            'AccountDeleteConfirm': "¿Estás seguro de que quieres eliminar tu cuenta? Esta acción no se puede deshacer.",
            'AccountUpdateSuccess': "Tu información de contacto ha sido actualizada con éxito.",
            'AccountUpdateFailure': "No se pudo actualizar tu información de contacto.",
            'AccountChangePassword': "Cambiar Contraseña de Cuenta",
            'AccountConfirmDelete': "Confirmar Eliminación de Cuenta",
            'AccountContact': "Contacto de Cuenta",
            'Achieved': "Alcanzó",
            'AddMoreItems': "Añadir Más Productos",
            'AddNewCard': "Añadir Nueva Tarjeta",
            'AddNewDeliveryAddress': "Añadir Nueva Dirección de Entrega",
            'AddPassword': "Añadir Contraseña",
            'Added': "Añadido",
            'Address': "Calle",
            'Address1': "Dirección1",
            'AddressDir': "Dirección",
            'Addresses': "Direcciones",
            'AddressSaveError': "Hubo un error al actualizar su dirección. Inténtalo de nuevo.",
            'AddToDeal': "Añadir a la oferta",
            'AddDealToOrder': "Agregar oferta al pedido",
            'AddToFavorites': "Añadir a Favoritos",
            'AddToOrder': "Añadir",
            'AGE': "¿Tienes ?? años o más?",
            'Age13': "Sí, tengo 13 años o más.",
            "AllergenInfo": "Información sobre alérgenos",
            'Allergy': "Alergia",
            'AllergyPreferences': "Alergias",
            'ALLOW_COOKIES_BODY': "Valoramos su privacidad. Las cookies ayudan a mejorar el rendimiento y personalizar su experiencia. Administre sus preferencias a continuación.",
            'ALLOW_COOKIES_HEADER': "¿PERMITIR COOKIES?",
            'CookiePreference': "Preferencia de Cookies",
            'ALREADYAMEMBER': "¿YA ERES USUARIO?",
            'AlreadyHaveAnAccount': "¿Ya tienes una cuenta?",
            'AmountDue': "Saldo Pendiente:",
            'AmountRedeemCurrencyApplyToOrder': "Introduce el importe a aplicar al pedido",
            'AmountToUse': "Cantidad a Utilizar",
            'And': " y la ",
            'APP_DOWNLOAD_BANNER': "Banner de descarga de la aplicación",
            'APP_ICON_FOR': "Icono de la aplicación de {0}",
            'Applied': "Aplicado",
            'AppliedToOrder': "Aplicado al Pedido",
            'Apply': "Aplicar",
            'ApplyChanges': "Aplicar Cambios",
            'ApplyGiftCard': "Aplicar Tarjeta Regalo",
            'AppUpdateRequiredMessage': "Una nueva version esta lista. Actualiza ahora para disfrutar de las últimas funciones y mejoras.",
            'April': "04 - Abril",
            'AprilOnly': "Abril",
            'AptUnit': "N.º Apto. / N.º Unidad / N.º Habitación",
            'AptSuiteRoom': "Piso / Apartamento / Habitación",
            'AptSuiteRoomPlaceholder': "Piso, Apartamento, Habitación #",
            'AptUnitSuite': "Apartamento/Unidad/Suite",
            'AreYouSureSignOutOrderCleared': "¿Está seguro de que desea cerrar la sesión? Su pedido será borrado.",
            'ARIA_DECREASE_QUANTITY': "Disminuir cantidad",
            'ARIA_INCREASE_QUANTITY': "Aumentar cantidad",
            'ARIA_LEFT_HALF': "Mitad izquierda",
            'ARIA_QUANTITY_SELECTOR': "Selector de cantidad",
            'ARIA_RIGHT_HALF': "Mitad derecha",
            'ARIA_SCROLL_DOWN': "Desplazar hacia abajo",
            'ARIA_SCROLL_LEFT': "Desplazar hacia la izquierda",
            'ARIA_SCROLL_RIGHT': "Desplazar hacia la derecha",
            'ARIA_SCROLL_UP': "Desplazar hacia arriba",
            'ARIA_LOYALTY_REDEEM_TOGGLE': "Seleccione método de pago: Pagar con dólares o canjear con puntos",
            'ARIA_LOYALTY_REDEEM_TOGGLE_LOCKED': "Canjear con puntos no disponible. No hay suficientes puntos",
            'ASAP': "Lo antes posible",
            'ASAP_UNAVAILABLE': "El pedido inmediato (ASAP) no está disponible en este momento.",
            'ASAPEstimatedTime': "Lo Antes Posible (Aprox. {{minutes}} Minutos)",
            'August': "08 - Agosto",
            'AugustOnly': "Agosto",
            'AutocompleteText': "Introduzca más de 4 caracteres para autocompletar...",
            'AutoSuggestionsAvailable': "Sugerencias automáticas disponibles.",
            'AvailableAmount': "Cantidad Disponible:",
            'AVAILABLE_POINTS_TEMPLATE': "{{points_unit}} Disponibles",
            'AVAILABLE_POINTS_LABEL_WITH_POINTS': "{{points}} {{points_unit}} Disponibles",
            'AvailableRewardsPoints': "Puntos de Recompensa Disponibles",
            "AwayFromNext": "de tu próxima",
            'BACK_TO_HOME': "Volver a la Página de Inicio",
            'Balance': "Saldo",
            'BalanceAsOf': "Saldo a partir de",
            'BankedCurrency': "Saldo Acumulado",
            'BS': "Bahamas (+1)",
            'BillAddr': "Dirección de Facturación:",
            'Addr': "Direccion de Facturación",
            'BillStName': "Nombre de la calle de facturación",
            'BillStNo': "Número de la calle de facturación",
            'BillZip': "Código Postal",
            'Birthday': "Cumpleaños",
            'BoxTopMsg': "El descuento se aplicará a su pedido tan pronto como todos los artículos requeridos se agreguen al pedido.",
            'BrowserNotSupported': "Lamentamos las molestias, pero nuestro web no es compatible con las versiones de Internet Explorer<br>o Safari anteriores a la version 14. Estos navegadores están desactualizados o no son<br>compatibles y es posible que no muestren todas las funciones de nuestro sitio correctamente. <br>Recomendamos actualizar su navegador a la última versión o usar un navegador diferente como Google Chrome,<br>Mozilla Firefox o Microsoft Edge para obtener la mejor experiencia en nuestro sitio. Gracias por su comprensión.",
            'BuildYourDeal': "Construya su trato",
            'BusinessDayIsClosed': "El día hábil está cerrado.",
            'Call': "Llamar",
            'CallStore': "Por favor llame a la tienda @ %storePhoneNumber% y el lugar que pedido por teléfono.",
            'CalorieFractionMin': "?? cal. por un/a ??? ????",
            'CalorieFractionMinMax': "?? - ??? cal. por un/a ???? ?????",
            'CALORIES': "Calorías",
            'CaloriesPerFraction': "{{calorie}} cal. por 1/{{serving}} {{servingName}}",
            'CaloriesPerServing': "{{calorie}} cal. / {{servingName}}",
            'CalorieServingMin': "?? cal. por ???",
            'CalorieServingMinMax': "?? - ??? cal. por ????",
            'Cancel': "Cancelar",
            'CannotResolveAddress': "No se puede resolver la dirección",
            'CannotUseCouponTotalZero': "No se puede aplicar la recompensa cuando el total es $0. Intenta agregar algo a tu carrito.",
            'CardNo': "Número de Tarjeta",
            'CardNumber': "Número de Tarjeta",
            'CardOnFile': "Tarjeta en archivo",
            'CarMakeModelAndColor': "Marca, modelo y color del coche",
            'CarryoutLocation': "Local de {{order_type_name}}",
            'CarryoutMethod': "Método de {{order_type_name}}",
            'CART_ITEMS_REMOVED_NOTICE': "Todos los artículos han sido eliminados de tu carrito debido a falta de disponibilidad o restricciones de entrega.",
            'CART_ITEMS_PARTIAL_UNAVAILABLE_STORE_SWITCH': "Algunos artículos en tu carrito no están disponibles en la tienda o dirección de entrega seleccionada:<br>{{item_names}}¿Te gustaría continuar?<br>Esto eliminará los artículos no disponibles de tu carrito.",
            'CART_ITEMS_UNAVAILABLE_STORE_SWITCH': "Todos los artículos de tu carrito no están disponibles en la tienda seleccionada.<br>¿Te gustaría continuar con el cambio de tienda?<br>Esto eliminará todos los artículos de tu carrito.",
            'CART_UPDATED_NOTICE': "Tu Carrito Ha Sido Actualizado",
            'Cart_RemovedItemNotice': "{{item_name}} fue eliminado de tu pedido.",
            'Cart_ScreenReader_RemovedItemNotice': "Eliminado {{item_name}}",
            'ITEM_PRICING_UNAVAILABLE': "Lo sentimos, no pudimos calcular el precio de uno o más de estos artículos. Por favor, inténtalo de nuevo.",
            'ITEMS_NO_LONGER_AVAILABLE': "El artículo {0} ya no está disponible en el tamaño seleccionado.",
            'Cash': "Efectivo",
            'CASH_ROUNDING_MESSAGE': "Los pagos en efectivo pueden redondearse a un valor de {{roundingValue}}.",
            'CCNotInte': "Por favor tenga su tarjeta de crédito y de identificación listo para el conductor.",
            'CHALLENGE_PARTICIPATION_CONFIRMATION': "Ahora estás participando en: {{challenge_name}}",
            'Challenges': "Desafíos",
            'Change': "Cambio",
            'ChangeStore': "Cambiar Tienda",
            'ChangeDeliveryAddress': "Cambio de dirección de entrega",
            'ChangeOrderDateTime': "Cambiar fecha y hora del pedido",
            'ChangeOrderType': "Cambiar su Pedido",
            'ChangeOrderTypeAndStore': "Cambiar tipo de pedido y tienda",
            'ChangePassword': "Cambiar la Contraseña",
            'ChangeTime': "Cambiar la Hora",
            'CharNotAllowed': "Su contraseña contiene caracteres que no están permitidos.</br>Los caracteres especiales sólo permitidos son",
            'CHECKOUT': "Procesar mi pedido",
            'Checkout': "Checkout",
            'CheckOutTheMenuToFindSomethingYouLove': "Explora el menú para encontrar algo que te encante",
            'CHOOSE_1ST_HALF': "Elige la primera mitad",
            'CHOOSE_2ND_HALF': "Elige la segunda mitad",
            'ChooseMany': "Elige Varios",
            'ChooseOne': "Elige Uno",
            'ChooseStore': "Elige Tienda",
            'City': "Ciudad",
            'Close': "Cerrar",
            'CLOSE_BANNER': "Cerrar banner",
            'Closed': "Cerrado",
            'ClosedForTheHoliday': "Cerrado por vacaciones",
            'ClosedOpensAt': "Cerrado - Abre el {weekday} a las {time}",
            'ComingSoon': "Próximamente",
            'Comment': "Comentario",
            'Complete': "Completo",
            'CompleteIncompleteDiscounts': "Complete los criterios de artículos de su descuento actual antes de agregar más recompensas.",
            'CompleteRequiredItems': "Complete los Elementos Requeridos",
            'CONFIRM_DELETE_ADDRESS': "¿Estás seguro de que quieres eliminar {{address}}?",
            'ConfirmPassword': "Confirmar Contraseña",
            'ConfirmNewPassword': "Confirmar Nueva Contraseña",
            'Conf1': "Su pedido ha sido recibido correctamente.",
            'Conf2': "Usted sera informado por correo electrónico tan pronto como la tienda haya confirmado su pedido en ??.",
            'Conf3': "Usted será informado por correo electrónico tan pronto como la tienda haya confirmado su pedido.",
            'Conf4': "Número de transacción",
            'Conf5': "Número de su pedido",
            'Confirm': "Confirmar",
            'ConfirmationPromiseTime': "{{time}} ({{minute_low}} - {{minute_high}} Minutos)",
            'Contact': "Contacto",
            'ContactInformation': "Información Del Contacto",
            'Contactless': "Entrega sin contacto",
            'ContactlessInfo': "La entrega sin contacto no está disponible para pagos en efectivo.",
            'ContactUs': "Contacta con Nosotros",
            'Continue': "Continuar",
            'ContinueAsGuest': "Continuar Como Invitado",
            'ContinueToPayment': "Continuar Al Pago",
            'Copyright': "© 2015 - %year% Adora POS, LLC. Reservados todos los derechos.",
            'CouldNotGetOrderNumber': "No se pudo obtener el número de pedido. Inténtalo de nuevo.",
            'Countdown': "Cuenta atrás",
            'Country': "País",
            'CountryCode': "Código de País",
            'CouponApplied': "Cupón Aplicado",
            'CouponCannotApplyMultiple': "Este cupón no se puede aplicar varias veces.",
            'CouponFutureOrderExpire': "El cupón no es válido para pedidos futuros.",
            'CouponCode': "Código promocional",
            'REMOVE_FUNDRAISER_FOR_PROMO_CODE': "Para usar un código de promoción, primero elimina cualquier código de recaudación de fondos aplicado.",
            'CouponDisclaimer': "No es válido con otras ofertas, descuentos o promociones, recaudación de fondos. válida para llevar, comer en el restaurante y el parto. se aplican los gastos de envío.",
            'LOCATION_OFFER_UNAVAILABLE': "Esta oferta no está disponible en esta ubicación.",
            'CouponNotQualified': "Cupón no califica",
            'CouponRedeemed': "El cupón ya ha sido canjeado.",
            'CP_DeleteLoyProfileConfirmation': "¿Está seguro de que desea eliminar su perfil de fidelización?<br>Esto no se puede deshacer.",
            'CPNLIMIT': "No hay límite del ?? por pedido de cupón seleccionado.",
            'CpnLimitOne': "¡Ups! Solo se puede canjear un cupón por pedido a la vez. Inténtalo de nuevo.",
            'CPNMINAMT': "Se requiere un pedido mínimo de ?? para el cupón seleccionado.",
            'CpnOTNotOffered': "Los descuentos aplicados no se ofrecen en el tipo de pedido seleccionado. Si se selecciona Continuar, se eliminarán los descuentos.",
            'CreateAccount': "Crear Perfil",
            'CreateAccountConfirmationPage': "Crear Perfil",
            'CreateAFavorite': "Crear un Favorito",
            'CreateAFavorite_BodyText': "Agregue cualquier comida de los pedidos recientes para volver a pedirla fácilmente.",
            'CreditCard': "Tarjeta de crédito",
            'CreditCardEndingWith': "{{card_type}} terminada en {{last_four}}",
            'CreditcardExpectCall': "Recibirá una llamada de la tienda para obtener la información de su tarjeta de crédito.",
            'CreditcardPhotoID': "Tenga a mano la tarjeta de crédito y una identificación válida presente al recibir el pedido.",
            'CreditCardPreAuthNote': "Nota: Esta tarjeta de crédito tiene preautorización para esta compra; El pago final se cobrará en la tienda al momento de la recogida.",
            'CreditCardPrepareInfo': "Tarjeta de crédito (por teléfono)",
            'CreditCardUponDelivery': "Tarjeta de Crédito en el Momento de la Entrega",
            'CreditDebit': "Crédito/Débito",
            'CreditHistory': "Historia de Crédito",
            'Curbside': "Recogida en la Acera",
            'Curbside_CarMakeModel': "La marca y el modelo de su coche",
            'Curbside_ConfirmCarModel': "Por favor, confirme la marca, el modelo y el color de su coche.",
            'Curbside_EstimatedDistanceFromRestaurant': "La distancia estimada desde el restaurante es ?? ???.",
            'Curbside_SelectStallNumber': "Seleccione su número de puesto de estacionamiento.",
            'Curbside_StoreHasBeenNotified': "Su solicitud ha sido enviada a la tienda. Cuando tu pedido esté listo, te lo llevaremos.",
            'Curbside_StoreHasBeenNotifiedAlready': "La tienda ya ha sido notificada.",
            'Curbside_StoreWillBeNorified': "La tienda será notificada de que estás aquí.",
            'Curbside_LocationAccessDisabled': "El acceso a la ubicación esta deshabilitado, por tanto no es posible calcular la distancia a la tienda.",
            'CurbsideInfo': "¡Permítanos llevar su pedido a su automóvil! Tire hacia el dosel blanco. Le indicaremos dónde estacionar y sacaremos su comida cuando esté lista. Debe pagarse con tarjeta de crédito al momento del pedido. ¡Gracias por ayudarnos a mantenernos a salvo!",
            'Curbside_ConfirmationError': "Ocurrió un error al confirmar la recogida en la acera. Por favor, inténtalo de nuevo más tarde.",
            'CurrentPassword': "Contraseña Actual",
            'Custom': "Otro",
            'CustomTip': "Propina Personalizada",
            'Customize': "Personalizar",
            'CustomizeItem': "Personalizar Artículo",
            'Date': "Fecha",
            'DateInPast': "La fecha no debe estar en el pasado.",
            'Day': "Día",
            'DealBuilder': "Constructor de Ofertas",
            'DealBuilderButtonDescription': "Seleccione y personalice los artículos necesarios para crear su oferta y agregarlos a su pedido.",
            'December': "12 - Diciembre",
            'DecemberOnly': "Diciembre",
            'DefaultAddress': "Hacer que esta sea mi dirección predeterminada",
            'DefaultModifierUnavailable': "{0} no está disponible ahora.",
            'Delete': "Eliminar",
            'DeleteAccount': "Eliminar cuenta",
            'DeleteMyAccount': "Eliminar mi Cuenta",
            'DeletePaymentConfirm': "¿Estás seguro de que deseas eliminar la tarjeta que termina en",
            'DeletePaymentFailure': "No se pudo eliminar tu método de pago.",
            'DeletePaymentSuccess': "Tu método de pago ha sido eliminado exitosamente.",
            'DeleteProfile': "Borrar perfil",
            'DeleteProfileMessage': "¿Estás seguro de que quieres eliminar tu perfil?</br>Se trata de una eliminación permanente de todos los registros de su perfil,</br>incluido el historial de pedidos, los puntos de fidelidad,</br>las recompensas obtenidas, las ofertas existentes y los pedidos.</br></br>Ingrese su contraseña para confirmar que desea eliminar su perfil.",
            'DeliverTo': "Entregar a",
            'Delivery': "Entrega",
            'DeliveryAddress': "Dirección de Entrega",
            'DeliveryAddressOutsideAllZonesError': "La entrega no está disponible para la dirección proporcionada, ya que se encuentra fuera del área de entrega de cualquier ubicación. Por favor, elige una dirección diferente o selecciona otra opción de pedido.",
            'DeliveryAddressIncompleteAddressError': "Introduce una dirección completa con un número de casa (Ej.: '123 Main St').",
            'DeliveryCharge': "Coste de envío",
            'DeliveryInstructions': "Instrucciones de Entrega",
            'DeliveryInstructionsPlaceholder': "Ej.: usa la puerta trasera, código de acceso",
            'DeliveryOutLimitedTimeError': "Actualmente Solo Ofrece Pedidos Anticipados",
            'DeliveryTime': "Hora de Entrega",
            'DeliveryTo': "Dirección de {{order_type_name}}",
            'DelLastAddr': "No se puede borrar. Una dirección debe permanecer en el archivo.",
            'DietaryPreferences': "Preferencias alimentarias",
            'DigitalGC': "Tarjeta regalo digital",
            'DineIn': "En restaurante",
            'DineInAt': "Comer en",
            'Discount': "Descuento",
            'Discounted': "Descuento",
            'DiscountedTotal': "Total Descontado",
            'Discounts': "Descuentos",
            'Donate': "Donar",
            'Donation': "Donación",
            'Done': "Hecho",
            'DontContinue': "No continúes",
            'DontForget': "¡NO LO OLVIDES!",
            'DontHaveAnAccount': "¿No tienes una cuenta?",
            'DontSave': "No Guardar",
            'DowngradedTo': "Descendió",
            'Due': "Debida",
            'EarnedPTS': "Ganó",
            'EarnedREW': "Consiguió",
            'EARN_REWARDS_ON_ORDER': "Ganarás <strong>{{points}} {{points_unit}}</strong> una vez completes tu pedido. Ofertas adicionales a continuación.",
            'EARN_REWARDS_ON_ORDER_NO_ITEMS': "Añade productos a tu carrito para empezar a ganar {{points_unit}} con este pedido.",
            'EARN_REWARDS_SIGNUP': "Empieza a ganar {{points_unit}} para canjearlos por tus productos favoritos del menú. ¡Podrías ganar <strong>{{points}} puntos</strong> con este pedido si te registras!",
            'EARN_REWARDS_SIGNUP_NO_ITEMS': "Añade productos a tu carrito y regístrate para empezar a ganar {{points_unit}} para tus productos favoritos del menú.",
            'Edit': "Editar",
            'EditContact': "Editar Contacto",
            'EditPreferredDeliveryAddress': "Editar Dirección de Entrega Preferida",
            'EditPreferredCarryOutStore': "Editar Tienda Preferida para Recoger",
            'EditingDiscountedItem': "La edición de un artículo con descuento eliminará el descuento. Puede volver a aplicar el descuento una vez que haya terminado de editar. ¿Quieres continuar con la edición del artículo?",
            'Email': "Correo Electrónico",
            'EMAIL_ALREADY_EXISTS': "El correo electrónico ya existe.",
            'EmailAddress': "Dirección de Correo Electrónico",
            'EmailNoMatch': "La direccion de correo electrónico que ha introducido no conciden.",
            'EmailNotFound': "No se encontró la dirección de correo electrónico '??' en nuestros registros.",
            'EmailOrPhoneNumber': "Correo Electrónico o Número de Teléfono",
            'EmailPlaceHolder': "correo@ejemplo.com",
            'EmptyDate': "La fecha no debe estar vacía.",
            'EmptyMemberList': "Debe agregar al menos un miembro para un pedido grupal.",
            'EmptyTime': "El tiempo no debe estar vacío.",
            'EndingBalance': "Balance Final",
            'EndPrivTerm': ".",
            'EnoughPoints': "Usted no tiene suficientes puntos para canjear el elemento seleccionado.",
            'ENTER_CODE': "Introduce el Código",
            'ENTER_FUNDRAISER_CODE': "Por favor, introduce un código de recaudación de fondos para aplicar.",
            'ENTER_FUNDRAISER_NAME': "Por favor, introduce el {{fundraiser_name}}",
            'EnterDeliveryAddress': "Introduzca la dirección de entrega",
            'EnterPasscode': "Introduce el Código de Acceso",
            'EnterPasscodeExpiresInXMinutes': "Ingrese el código de acceso que se le envió por SMS.<br>El código de acceso caduca en 15 minutos.",
            'EnterYourPasswordPlaceholder': "Introduce tu contraseña",
            'ErrorGettingServerDate': "Error al obtener la fecha del servidor.",
            'Error_OrderExceededCashLimit': "Lamentablemente, este pedido ha superado el límite de pago en efectivo de nuestra tienda de {{money}}.<br>Sin embargo, puedes continuar con el pago utilizando una tarjeta de crédito.<br>Pedimos disculpas por cualquier inconveniente que esto pueda causar.",
            'Error_ChangeOrderTypeCartError': "{{size}} {{item}} no está disponible para {{order_type}}.<br>Por favor, elimínalo de tu pedido antes de cambiar el tipo de pedido.",
            'Error_ConfigurationGenericError': "El sistema de pedidos en línea no está disponible en este momento debido a un problema de configuración.<br>Por favor, inténtelo de nuevo más tarde o póngase en contacto directamente con la tienda para obtener ayuda.<br>Lamentamos las molestias.",
            'ERROR_COUPON_INELIGIBLE_FOR_USE_WITH_EVENT': "Los cupones no son válidos para su uso con {{event_name}}",
            'Error_GiftCardBalanceIs0': "Esta tarjeta regalo tiene un saldo de 0 y no puede utilizarse para el pago.",
            'Error_HalfNotAvailableForSelectedSize': "La opción de Mitad & Mitad no está disponible para el tamaño seleccionado.<br>Por favor, elija un tamaño u opción diferente.",
            'Error_ModifierIsNotBeingOfferedOnSize': "{{modifier}} está disponible para los siguientes tamaños:{{sizes}}.",
            'Error_NoItemsHaveBeenAddedToOrder': "No puedes proceder al pago porque no has añadido ningún producto a tu pedido.<br>Por favor, añade al menos un producto para continuar.",
            'Error_NoPaymentAttachedToOrder': "No se encontró ningún método de pago para este pedido. Añada o vuelva a seleccionar un método de pago antes de finalizar la compra.",
            'Error_OrderTypeUnavailableAtStore': "{{order_type}} no está disponible en la tienda seleccionada. Por favor, elige otra tienda que ofrezca este tipo de pedido.",
            'ERROR_PARTICIPATE_EVENT_REMOVE_COUPON': "Si deseas participar en {{event_name}}, por favor elimina tus cupones aplicados",
            'Error_ReachedMaxNumberOfModifiers': "Has alcanzado el número máximo de toppings permitidos. Por favor, ajusta tu selección para continuar.",
            'Error_ReachedMaxNumberOfModifiersOnItem': "Has alcanzado el número máximo de modificadores permitidos para este artículo.<br>Por favor, revisa tus selecciones.",
            'Error_ReservedTimeSlotExpired': "Tu franja horaria reservada ha expirado.<br>Tu pedido se procesará lo antes posible.<br>El tiempo estimado para {{order_type_name}} es de aproximadamente {{minutes}} minutos desde la confirmación del pedido.",
            'Error_ApplePayCannotStart': "No se puede iniciar Apple Pay. Por favor, intente con otro método de pago.",
            'Error_GooglePayCannotComplete': "No se puede completar Google Pay. Por favor, intente con otro método de pago.",
            'Error_SavedCardNotProperlySelected': "No pudimos confirmar su tarjeta guardada. No se preocupe, no se procesó ningún pago. Vuelva a seleccionar su tarjeta e inténtelo de nuevo.",
            'Error_SelectedModifierNoSizeSwitch': "{{modifier}} no está disponible para el/la {{item}} de tamaño {{size}}.<br>¿Te gustaría cambiar a un tamaño donde {{modifier}} esté disponible?",
            'Error_SelectToppings1Half': "Por favor, selecciona un topping para la primera mitad.<br>Por ejemplo, elige un topping de queso para una pizza de queso.",
            'Error_SelectToppings2Half': "Por favor, selecciona un topping para la segunda mitad.<br>Por ejemplo, elige un topping de queso para una pizza de queso.",
            'Error_UnableToFindProperSizeToApply': "No podemos identificar un tamaño adecuado para el artículo seleccionado.<br>Por favor, revise su selección e inténtelo de nuevo.",
            'Error_CustomizeUnable': "No se puede personalizar este artículo. Inténtelo de nuevo más tarde.",
            'Error_SelectSize': "Seleccione un tamaño.",
            'Error_StoreClosed': "La tienda está actualmente cerrada y no puede aceptar pedidos en este momento.<br>Por favor, intente nuevamente durante el horario comercial o contacte la tienda directamente para obtener asistencia.",
            'Estimated': "Estimado",
            'EstimatedDelivery': "Entrega Estimada",
            'EstimatedDeliveryTime': "Tiempo Estimado de Entrega",
            'EstimatedOrderTime': "Tiempo Estimado de Pedido",
            'ETA_ORDER_TYPE_TIME_IS': "La hora estimada de {{order_type}} es {{time}}",
            'ETA_TIME_WITH_MINUTES': "{{time}} ({{formatted_minutes}})",
            'ExistingCreditCard': "Tarjeta de Crédito Existente",
            'ExistingCustomerSignIn': "Inicia sesión con tu cuenta",
            'ExpDate': "Fecha de Caducidad",
            'ExpirationDate': "Fecha de Caducidad",
            'Expired': "Caducado",
            'Expires': "Válido hasta",
            'ExtraToppings': "Ingredientes adicionales",
            'FailUpdateInfo': "No se puede actualizar la información en este momento.",
            'FavoriteOrders': "Pedidos Favoritos",
            'Favorites': "Favoritos",
            'FavoriteStores': "Tiendas Favoritas",
            'FavoriteMenuItems': "Artículos Favoritos del Menú",
            'FavoriteLocations': "Ubicaciones Favoritas",
            'FavoriteItemModalDescription': "¿Quieres añadir este artículo a tus favoritos?",
            'FavoriteItem': "Artículo Favorito",
            'FavoriteItemNameRequiredError': "El nombre del artículo favorito es obligatorio.",
            'FavoriteItemNameLengthError': "El nombre del artículo favorito no puede exceder los 100 caracteres.",
            'FavoriteItemMissingMod': "El artículo favorito no se puede agregar porque faltan modificaciones.",
            'FavoriteItemSave_Error': "Hubo un error al guardar el artículo favorito. Inténtalo de nuevo.",
            'FavoriteItemSave_Success': "El artículo favorito se guardó correctamente.",
            'FavoriteItemUnavailable': "El artículo favorito no está disponible en este momento.",
            'FavoriteItem_Delete_Error': "Hubo un error al eliminar el artículo favorito. Inténtalo de nuevo.",
            'FavoriteItem_Delete_Confirm': "¿Estás seguro de que deseas eliminar este artículo de tus favoritos?",
            'FavoriteObjectNone': "No hay {{object}} favoritos en este momento",
            'FavoriteObjectInstruction': "Agrega cualquier {{object}} de los pedidos recientes para volver a pedirlo fácilmente.",
            'favoriteStoreDelete_Error': "Hubo un error al eliminar la tienda favorita. Inténtalo de nuevo.",
            'favoriteStoreSaveName_Error': "Hubo un error al guardar la tienda favorita. Inténtalo de nuevo.",
            'favoriteStoreDelete_Confirm': "¿Estás seguro de que deseas eliminar esta tienda de tus favoritos?",
            'FavStore': "Haz de esta tienda mi tienda favorita",
            'February': "02 - Febrero",
            'FebruaryOnly': "Febrero",
            'feet': "pies",
            'Find': "Buscar",
            'FindStore': "Encuentra una Tienda",
            'FinishCoupon': "Terminar Cupón",
            'FinishX': "Por favor, Caja dentro de los ?? minutos.",
            'FirstHalf': "Primera Mitad",
            'FirstName': "Nombre",
            'For': "por",
            'ForgotPassword': "¿Has Olvidado tu Contraseña?",
            'ForgotPasswordEmailError': "Ingresa tu correo electrónico para restablecer tu contraseña.",
            'Foundation': "Fundación",
            'Franchising': "Franquicia",
            'Friday': "Viernes",
            'FulfillmentTime': "El tiempo de cumplimiento de este pedido se ha cambiado a Lo antes posible, ??? el tiempo es de aproximadamente ?? minutos después de que su pedido haya sido realizado.",
            'Fundraiser': "Recaudación de Fondos",
            'FundraiserCode': "Código de recaudación de fondos",
            'FUNDRAISER_CODE_APPLIED': "Your Fundraiser Code Has Been Applied.",
            'FUNDRAISER_CODE_ERROR_COUPON': "Para participar en la recaudación de fondos, elimina los cupones aplicados.",
            'FundraiserCodeOK': "Se ha aplicado su código de recaudación de fondos.",
            'FundraiserValueIsEmpty': "Campo incompleto. Por favor introduzca el ",
            'Fundraising': "Recaudación de Fondos",
            'Future': "Futuro",
            'GetDirections': "Obtener direcciones",
            'GIFT_CARD_REQUIRED': "Se requiere el número de la tarjeta de regalo.",
            'GIFT_CARD_PIN_REQUIRED': "Se requiere el PIN.",
            'GiftCard': "Tarjeta de regalo",
            'GiftCardAlreadyApplied': "Esta tarjeta regalo ya ha sido aplicada a tu pedido.",
            'GiftCardEndingWith': "Tarjeta regalo terminada en {{last_four}}",
            'GiftCardNumber': "Número de Tarjeta Regalo",
            'GiftCards': "Tarjetas de Regalo",
            'GO_ErrorDuringGOSave': "Ocurrió un error al guardar el pedido de grupo.",
            'GO_ErrorDuringGOSubmit': "Ocurrió un error durante el envío del pedido grupal.",
            'GO_FinalizeQuestionYesNo': "¿Finalizar el pedido de grupo y pasar a la caja?",
            'GO_FinalizeWillDeleteWarning': "Al finalizar el pedido de grupo, se eliminarán los pedidos de los miembros que no se hayan enviado.",
            'GO_NotStartedMembers': "de los pedidos de miembros no iniciados en este pedido de grupo pertenecen a:", // plural
            'GO_NotSubmittedMembers': "de los pedidos de miembros no enviados en este pedido de grupo pertenecen a:", // plural
            'GO_OrderFinalization': "<h1 class='warning'> Finalización del pedido</h1><br>El pedido de grupo se está finalizando.<br>Por favor, espere.",
            'GO_OrderUndefined': "El pedido del grupo no está definido.",
            'GO_UnsubmitOrders3': "Continuar enviando el pedido grupal hará que se eliminen los pedidos de los miembros no enviados y<br>ya no le permitirá agregar miembros a el pedido de grupo actual<br>¿Está seguro de que desea continuar con el pago?",
            'GoogleMapsErrorAmbiguous': "Error de Google Maps: direcciones ambiguas en",
            'GoogleMapsErrorResolvingAddress': "Error de Google Maps: error al resolver la dirección",
            'GoogleSignInFailed': "El inicio de sesión con Google no se completó. Inténtalo de nuevo o usa otro método de inicio de sesión.",
            'SignInWithGoogle': "Iniciar sesión con Google",
            'SignUpWithGoogle': "Registrarse con Google",
            'GoToMenu': "Ir Al Menú",
            'Gratuity': "Propina",
            'GROUPORDER': "PEDIDO DE GRUPO",
            'GroupOrderStatus1': "NO EMPEZADO",
            'GroupOrderStatus2': "EN CURSO",
            'GroupOrderStatus3': "PEDIDO",
            'GroupOrderStatus4': "NO PARTICIPANDO",
            'GroupOrderStatus5': "ENVIAR",
            'GroupOrderStatus6': "CANCELADO",
            'GroupOrderTime': "Tiempo del Pedido de Grupo",
            'GRPORD_AddMember': "Añadir Miembro",
            'GRPORD_AddNewGroupMember': "Agregar nuevo miembro del grupo",
            'GRPORD_AreYouSureCancel': "¿Está seguro de que desea cancelar su pedido de grupo?",
            'GRPORD_CancelFailed': "La cancelación del pedido grupal falló.",
            'GRPORD_CancelGroupOrder': "El pedido de grupo ha sido cancelado.",
            'GRPORD_CreateGroupOrder': "Crear un pedido de grupo",
            'GRPORD_CreateOrder': "Crear pedido de grupo",
            'GRPORD_Date': "Fecha",
            'GRPORD_PayAllDescr': "Si Pagar para todos los miembros no está marcado, cada miembro pagará su propia comida.",
            'GRPORD_PayAllMembers': "Pagar por todos los miembros",
            'GRPORD_SelectAll': "Seleccionar todos los miembros del grupo",
            'GRPORD_SelectMembers': "Seleccione miembros para su pedido de grupo",
            'GRPORD_Time': "Hora",
            'GRPORD_Welcome': "Invita a tus amigos a unirse a un pedido grupal.Cada miembro recibirá un enlace para pedir su comida y agregarla al grupo.",
            'Guest': "Continua como invitado",
            'HalfHalfPizzaOrderDetail': "Pizza Mitad y Mitad ??",
            'HalfAndHalf': "Mitad y Mitad",
            'HamburgerMenu': "Menú de Navegación",
            'HaveGiftCard': "¿Tiene una tarjeta de regalo?",
            'Hello': "Hola",
            'Hey': "Hola",
            'Hide': "Ocultar",
            'HIDE_DETAILS': "Ocultar Detalles",
            'HHP': "Pizza Mitad y Mitad",
            'Home': "Inicio",
            'Inactive': "Inactiva",
            'IncompleteProfileError': "Falta información en tu perfil. Por favor complete todos los campos para continuar.",
            'DeleteMyProfile': "Eliminar mi perfil",
            'InProgress': "En Progreso",
            'InStore': "En Tienda",
            'InStoreLimit': "Este pedido llegó al limite de pago de nuestra tienda ?? pero usted puede pagar con tarjeta de crédito. Nos disculpamos por cualquier inconveniente.",
            'Instructions': "Instrucciones",
            'ImportDigitalGiftCards': "Importar tarjeta de regalo digital",
            'INVALID_OR_EXPIRED_FUNDRAISER_CODE': "El código de recaudación de fondos ha caducado o no es válido.",
            'INVALID_BIRTHDAY': "Fecha de nacimiento inválida.",
            'InvalidAddr': "Dirección de facturación no válida.",
            'InvalidAddress': "La dirección no es válida",
            'InvalidAddressNoStreetNumber': "La dirección no es válida o le falta un número de calle. Por favor revíselo e inténtelo nuevamente.",
            'InvalidAddressNoZipCode': "La dirección no es válida o falta un código postal. Por favor revise y vuelva a intentarlo.",
            'InvalidAge13': "Debe reconocer que tiene al menos 13 años de edad.",
            'InvalidCardNumber': "El número de tarjeta regalo introducido no es válido. Por favor, revísalo e inténtalo de nuevo.",
            'InvalidCarInfo': "Marca, modelo y color del coche son obligatorios.",
            'InvalidCC': "Número de tarjeta de crédito no es válido.",
            'InvalidCouponRequired': "Por favor, introduzca un código promocional para aplicar.",
            'InvalidCode': "¡Código inválido!",
            'InvalidCommonSize': "Tamaño común no válido.",
            'InvalidCoupon': "El cupón ha caducado o no es válido.",
            'InvalidCouponCode': "Código promocional no es válido",
            'InvalidCouponDate': "El cupón no es válido ni canjeable en este momento para este pedido.",
            'InvalidCVV': "CVV no es válido.(codigo de verificación de la tarjeta)",
            'InvalidDate': "Fecha invalida.",
            'InvalidDay': "Debe seleccionar un día.",
            'InvalidEmail': "Dirección de correo electrónico es invalida.",
            'InvalidEmptyEmail': "Se requiere una dirección de correo electrónico.",
            'InvalidOrExpiredPasscode': "Código de acceso no válido o caducado.",
            'InvalidEmailOrPassword': "Correo electrónico inválido y/o contraseña. Ingrese credenciales válidas o restablezca su contraseña",// o intente iniciar sesión con su número de teléfono.",
            'InvalidEmailOrPasswordNoPhone': "Correo electrónico inválido y/o contraseña. Ingrese credenciales válidas o restablezca su contraseña.",
            'InvalidEmailPhone': "Dirección de correo electrónico o número de teléfono inválido.",
            'InvalidExp': "La fecha de caducidad no es válida.",
            'InvalidFirstName': "Nombre inválido.",
            'InvalidEmptyFirstName': "Se requiere un nombre.",
            'InvalidFundraiserCode': "Código de recaudación de fondos no válido.",
            'InvalidLastName': "Invalido apellido.",
            'InvalidEmptyLastName': "Se requiere un apellido.",
            'InvalidMonth': "Por favor seleccione un mes",
            'InvalidOrderTypeCode': "El cupón no es válido para la ??.",
            'InvalidPassword': "La contraseña debe contener al menos un número, una letra minúscula, una letra mayúscula, un símbolo (* + @ # $ % & !) y un mínimo de ocho caracteres.",
            'InvalidPasswordLogin': "Contraseña invalida.",
            'InvalidPasswordMatch': "Las contraseñas no coinciden.",
            'ConfirmEmailAddress': "Confirmar Dirección de Correo Electrónico",
            'EmailsMustMatch': "Los correos electrónicos deben coincidir.",
            'ConfirmEmailPasteWarning': "Para mayor precisión, por favor escriba su dirección de correo electrónico en lugar de pegarla.",
            'InvalidPhone': "Número de teléfono es inválido.",
            'InvalidEmptyPhone': "Se requiere un número de teléfono.",
            'InvalidPin': "PIN no válido. Por favor, introduce un PIN válido (mínimo 1 dígito) e inténtalo de nuevo.",
            'InvalidPromotionCode': "Código de promoción no válido.",
            'InvalidReward': "La recompensa no es válida ni canjeable en este momento del pedido.",
            'InvalidScheduleCode': "El cupón no es válido hoy /  ahora.",
            'InvalidStName': "Nombre de la calle de facturación no válida.",
            'InvalidTermsAndPrivacy': "Debe aceptar los Términos de Uso y la Política de Privacidad antes de realizar su pedido.",
            'InvalidTimeNotSelected': "Selecciona una hora diferida para continuar.",
            'InvalidTimeOutOfRange': "Hora inválida. Por favor, seleccione una hora válida.",
            'InvalidTip': "Cantidad de propina no válida.",
            'InvalidZip': "Inválido código postal de facturación.",
            'InvalidZip1': "Código postal no válido.",
            'InvalidZipCode': "Código postal no válido.",
            'Item': "Artículo",
            'ItemAdded': "Agregar al pedido",
            'ItemComment': "Nota: Los ingredientes y / o códigos de cupón agregados en el cuadro <strong> NO </strong> se aplicarán al artículo.",
            'Items': "Artículos",
            'ItemsAddedSuccess': "Artículo añadido con éxito a tu pedido.",
            'ItemsAddedToCart_Single': "1 artículo añadido a la cesta",
            'ItemsAddedToCart_Plural': "{{number}} artículos añadidos a la cesta",
            'ItemSizeNotHalfHalf': "??? no está disponible en el tamaño seleccionado.El tamaño del artículo se ha actualizado para ??.",
            'January': "01 - Enero",
            'JanuaryOnly': "Enero",
            'Join': "Unirse",
            'Joined': "Unido/a",
            'JoinLoyaltyProgramButton': "Únase al Programa de Lealtad",
            'JoinLoyaltySignUpConfirmPrivacy': "Política de Privacidad",
            'JoinLoyaltySignUpConfirmTerms': "Términos de Uso",
            'JoinOurLoyaltyProgram': "Únase a nuestro programa de fidelización y<br/>gane recompensas y ofertas exclusivas.",
            'JoinOurTeam': "Únete a nuestro equipo",
            'JoinRewards': "Unirme a las Recompensas",
            'JoinRewardsProgram': "Unirme al Programa de Recompensas",
            'JsonError': "Error.</br>El análisis de JSON petición ha fallado.!",
            'July': "07 - Julio",
            'JulyOnly': "Julio",
            'June': "06 - Junio",
            'JuneOnly': "Junio",
            'kilometer': "kilómetro",
            'kilometers': "kilómetros",
            'kilometer_short': "km",
            'LangAbbr': "ES",
            'LangHeader': "Selecciona tu idioma",
            'LastName': "Apellido",
            'LastOrderDate': "Fecha del Último Pedido",
            'LastTransaction': "Última transacción",
            'Later': "Realizar pedido ahora para más tarde",
            'LearnMore': "Aprende más",
            'Left': "Izquierda",
            'LifetimePoints': "Puntos Conseguidos",
            'EmailMeUpdatesAboutMyOrder': "Envíenme actualizaciones de mi pedido por correo electrónico",
            'TextMeUpdatesAboutMyOrder': "Envíenme actualizaciones de mi pedido por mensaje de texto",
            'LIFETIME_POINTS_TEMPLATE': "{{points_unit}} Acumulados",
            'LikeToReceiveOrderStatusNotificationsViaEmail': "Me gustaría recibir notificaciones del estado del pedido por correo electrónico:",
            'LikeToReceiveOrderStatusNotificationsViaEmailAndText': "Me gustaría recibir notificaciones del estado del pedido por correo electrónico y mensajes de texto:",
            'LikeToReceiveOrderStatusNotificationsViaText': "Me gustaría recibir notificaciones del estado del pedido por mensajes de texto:",
            'LIMITHOUS': "Horas limitadas",
            'Loading': "Cargando...",
            'LocationNotFound': "Ubicación no encontrada.Pruebe con una ciudad o código postal.",
            'Locations': "Tiendas",
            'Locations_Heart_NoName_Error': "Debe insertar un nombre de tienda. Inténtalo de nuevo.",
            'Locations_Heart_SaveLocationName_NotSignedIn': "Para crear una tienda favorita, debe iniciar sesión o crear un perfil.</br>¿Le gustaría ser dirigido a la página de inicio de sesión?",
            'Locations_Heart_SaveLocationName_RemoveHeart': "¿Estás seguro/a de que quieres eliminar la tienda favorita?",
            'Locations_Heart_SaveLocationName_SignedIn': "¿Te gustaría ponerle un nombre a esta tienda?",
            'Locations_Search': "Buscar: Ciudad, Estado, o Código Postal...",
            'LocationServicesBlocked': "Sus servicios de ubicación están bloqueados. Para que la tienda encuentre su ubicación rápidamente, conceda acceso a la ubicación.",
            'LocationSelector': "Selector de Ubicación",
            'LogIn': "Iniciar sesión",
            'Login': "Iniciar Sesión",
            'LogonError': "Pasó de error de inicio de sesión Desconocido. Por favor, vuelva a escribir su dirección de correo electrónico y contraseña a continuación, intente iniciar sesión de nuevo.",
            'LogOut': "Log Out",
            'LoyaltyAvailablePoints': "{{points}} Puntos Disponibles",
            'LoyaltyCantReachInfoError': "¡Ups! No podemos acceder a tu información de lealtad,<br>por favor inténtalo de nuevo más tarde.",
            'LoyaltyCarrierMsg': "*se pueden aplicar cargos por mensajería de texto del operador",
            'LoyaltyCreateWelcome': "¡Este es nuestro nuevo programa de fidelización!",
            'LoyaltyRewardsCannotCombineWithBundledOffers': "Las recompensas de fidelización u ofertas no se pueden combinar con ofertas agrupadas.",
            'LoyaltyMemberSince': "Miembro de lealtad desde",
            'LOYALTY_POST_ORDER_FAILED': "¡Tu pedido se realizó correctamente! Tus puntos de lealtad aún se están procesando y se agregarán a tu cuenta en breve.",
            'LoyaltyServerOffline': "¡Ups! Algo no está funcionando. Por favor, inténtalo de nuevo más tarde.<br>Si el problema persiste, por favor continúa como invitado y realiza tu pedido.",
            'LoyaltyStoreMsg': "Si te gusta unirte ??? situado en ???? programa de lealtad, ingrese su número de teléfono celular a continuación.",
            'LoyaltyVerifyMsg': "Se enviará un código de verificación a su teléfono",
            'MakeItAGroupOrder': "Conviértalo en un Pedido de Grupo",
            'Makeithalfhalfpizza': "Puede pedir mitad y mitad de la pizza",
            'MakeItHalfAndHalf': "Hazlo Mitad y Mitad",
            'MakeMyDefaultStore': "Haz Mi Tienda Predeterminada",
            'March': "03 - Marzo",
            'MarchOnly': "Marzo",
            'MarkAttributesDidNotSaved': "No se han guardado los atributos del programa de fidelización. Utilice la función de perfil de cliente para actualizarlos.",
            'MarkEmail': "Email",
            'MarkInfo': "Me gustaría recibir ofertas e información de productos a través de:",
            'MarkSelDay': "Seleccione un dia",
            'MarkSelMonth': "Selecciona un mes",
            'MarkText': "SMS",
            'MarketingEmail': "Manténme actualizado con ofertas exclusivas a través de Correo Electrónico",
            'MarketingText': "Manténme actualizado con ofertas exclusivas a través de Mensaje de texto",
            'MarketingSurvey': "Suscríbete para recibir correos electrónicos de encuestas de pedidos y tener la oportunidad de obtener descuentos adicionales.",
            'May': "05 - Mayo",
            'MayOnly': "Mayo",
            'MeetPromiseTimeError': "No podemos cumplir con el tiempo prometido del pedido. Por favor llame a la tienda @ %storePhoneNumber%",
            'MeetPromiseTimeWarning': "El almacén dejará de aceptar órdenes de ?? en línea en 5 minutos. Por favor, Caja dentro de los 5 minutos.",
            'Menu': "Menú",
            'MenuItems': "Elementos del Menú",
            'MfaChallengeExpired': "Tu verificación ha caducado. Vuelve a iniciar sesión.",
            'MfaChallengeExpiredAccount': "Tu verificación ha caducado. Inténtalo de nuevo.",
            'MfaCodeInvalid': "Ese código no es correcto. Inténtalo de nuevo.",
            'MfaCodeLabel': "Código de verificación",
            'MfaCodePlaceholder': "Código de 6 dígitos",
            'MfaCodeResent': "Se envió un nuevo código. Válido por 15 minutos.",
            'MfaCodeSubtitle': "Código enviado &middot; válido por 15 minutos",
            'MfaCodeTitle': "Ingresa tu código",
            'MfaSentTo': "Enviado a {0}",
            'MfaTooManyAttempts': "Demasiados intentos de verificación. Inténtalo de nuevo más tarde.",
            'MfaTrustBrowser': "Confiar en este navegador durante 30 días",
            'MfaVerify': "Verificar",
            'MfaVerifyAndSignIn': "Verificar e iniciar sesión",
            'MfaVerifySubtitle': "Por tu seguridad, confirma tu correo electrónico o número de teléfono para continuar iniciando sesión.",
            'MfaVerifySubtitleAccount': "Por tu seguridad, confirma tu correo electrónico o número de teléfono para continuar.",
            'MfaVerifyTitle': "Verifica que eres tú",
            'MX': "México (+52)",
            'mile_short': "mi",
            'mile': "milla",
            'miles': "millas",
            'MinDelAmt': "Esta compra está por debajo del monto mínimo de pedido para la entrega de $??. Inténtalo de nuevo.",
            'REQUIRED_MODIFIER_GROUP_MINIMUM': "Debes seleccionar al menos {amount} opción(es) de {display_group}",
            'Minutes': "Minutos",
            'Minutes_Number': "{{minutes}} Minutos",
            'MISSING': "Hay falta de información, por favor complete antes de realizar su pedido.",
            'ModifierLeftSide': "{{modifier_name}} Left Izquierdo",
            'ModifierPremodifierLeftSide': "Selección de premodificador de {{modifier_name}}, lado izquierdo",
            'ModifierPremodifierRightSide': "Selección de premodificador de {{modifier_name}}, lado derecho",
            'ModifierRightSide': "{{modifier_name}} Lado Derecho",
            'Monday': "Lunes",
            'ModifierIsNotCompatible': "??? no se ofrece en la mitad seleccionada.",
            'Month': "Mes",
            'MultipleMemberOrderWindowAlert': "Se ha abierto en otra sesión de pedido para este miembro. Esta ventana será redirigida a la página de inicio de la tienda.",
            'MYGROUPORDER': "MI PEDIDO DE GRUPO",
            'MyAccount': "Mi Cuenta",
            'MyAccountPage': "Página de Mi Cuenta",
            'MyAvailableGiftCards': "Mis tarjetas de regalo disponibles",
            'MyCredits': "Mis Créditos",
            'MyHistory': "Mi Historial",
            'MyHistory_FirstTimeMessage': "Todo tu historial estará disponible aquí. ¡Comienza a acumular recompensas hoy!",
            'MyMarcosRewards': "Mis Marco's Recompensas",
            'MyMembership': "Mi Membresía",
            'MyOffers': "Mis ofertas",
            'MYORDER': "MI PEDIDO",
            'MyOrders': "Mis Pedidos",
            'MyOrders2_FavoriteIconClick': "Nombra tu pedido favorito",
            'MyOrders2_FetchOrderData_Error': "Hubo un error al recuperar sus pedidos. Inténtalo de nuevo.",
            'MyOrders2_FetchOrderDetails_Error': "Hubo un error al recuperar los detalles de su pedido. Inténtalo de nuevo.",
            'MyOrders2_ToggleFavoriteOrder_Error': "Se produjo un error al guardar los cambios de pedido favoritos. Inténtalo de nuevo.",
            'MyOrders_ReOrder_AllError': "Estos artículos no están disponibles actualmente. Puede seleccionar otros elementos en su lugar.",
            'MyOrders_ReOrder_MultipleError': "actualmente no están disponibles. Puede seleccionar otro elemento en su lugar.",
            'MyOrders_ReOrder_SingleError': "no está disponible. Puede seleccionar otro elemento en su lugar.",
            'MyPoints&Rewards': "Mis Puntos y Recompensas",
            'MyPoints': "Mis Puntos",
            'MyProfile': "Mi Perfil",
            'MyRewards': "Mis Recompensas",
            'MyRewardsAndOffers': "Mis Recompensas y Ofertas",
            'MyRewardsSlogan': "<div>Mis</div><div>Recompensas</div>",
            'Name': "Nombre",
            'NameYourItem': "Nombra tu artículo",
            'Nearby': "Cerca",
            'NearbyRestaurants': "Tiendas Cercanas",
            'NewAddress': "Nueva direccion",
            'NewDelAddr': "Introduzca la nueva dirección de entrega.",
            'NEW_ADDRESS_UPDATED_SUCCESSFULLY': "Nueva dirección de {} actualizada correctamente.",
            'NewPassword': "Introduzca Nueva Contraseña",
            'Next': "Siguiente",
            'No': "No",
            'NoAddressesAddedToAccount': "No hay direcciones agregadas a su cuenta",
            'NoAvailableGiftCards': "No tienes tarjetas regalo disponibles.",
            'NoAvailableRewardsOrOffers': "No tienes recompensas ni ofertas disponibles.",
            'NoCardsAddedToAccount': "No hay tarjetas agregadas a su cuenta",
            'NoCreditHistory': "No hay historial de crédito registrado.",
            'NoDeliveryAddress': "Ninguna dirección de entrega ha sido seleccionada.",
            'NoDiscountAssociatedWithCode': "No hay descuento asociado con este código.",
            'NoFavoriteItems': "No tienes favoritos para mostrar.",
            'NoFutureOnlineForSelectedDate': "Los pedidos diferidos no están disponibles para la fecha seleccionada.<br>Por favor, elige otra fecha o contacta con la tienda para más información.",
            'NoFutureOnlineOrder': "La tienda no acepta pedidos en línea diferidos en la fecha seleccionada.",
            'NoImNot': "No.",
            'NoItemAdded': "No se ha añadido ningún artículo al pedido.",
            'NoItemsAvailable': "Ninguno de los artículos de este pedido está disponible actualmente.",
            'NoMenuItemsHaveBeenSelected': "No Se Ha Seleccionado Ningún Elemento del Menú",
            'None': "Ninguno",
            'NO_OFFERS': "No Hay Ofertas",
            'NoOffers': "Aún no hay ofertas.",
            'NoOnlineNow': "Almacenar en no aceptar pedidos en línea ahora. sin embargo es posible hacer un pedido para el día de hoy.",
            'NoOtherStore': "No (Seleccionar otra ubicación)",
            'NoPayment': "Su pago no ha sido seleccionado.",
            'NoPreferredDeliveryAddress': "No hay Dirección de Entrega Preferida.",
            'NoPreferredCarryOutStore': "No hay Tienda Preferida para Recoger.",
            'NoRecentOrders': "No hay pedidos recientes.",
            'NoRedeemItemGroups': "No hay recompensas disponibles para canjear todavía. ¡Sigue ganando puntos con cada pedido!",
            'NoRewards': "No tienes recompensas para mostrar.",
            'NoRewardsActivity_Single': "No hay actividad de recompensas en el último día",
            'NoRewardsActivity_Plural': "No hay actividad de recompensas en los últimos {{number}} días",
            'NoRewardsAvailableInGroup': "No hay recompensas disponibles actualmente en este grupo.",
            'NoSavedPaymentMethods': "No hay Métodos de Pago Guardados.",
            'NOTAMEMBER': "¿NO ERES MIEMBRO?",
            'NotEnrolled': "No se han inscrito en el programa de fidelización",
            'NoThanks': "No, Gracias",
            'Notifications': "Notificaciones",
            'notifsAnd': " y ",
            'notifsConsent': "Me gustaría recibir notificaciones sobre el estado del pedido por ",
            'notifsEmail': "correo electrónico",
            'notifsText': "SMS",
            'NoTime': "Por desgracia no hay tiempo suficiente para procesar un pedido ??? online.",
            'NoTip': "Sin Propina",
            'NotOfferedInSelStore': "No se ofrece ?? en la tienda seleccionada.",
            'November': "11 - Noviembre",
            'NovemberOnly': "Noviembre",
            'Now': "Ahora",
            'Number': "Número",
            'Nutrition': "Nutrición",
            'October': "10 - Octubre",
            'OctoberOnly': "Octubre",
            'off': "de descuento",
            'OFF': "De Descuento",
            'Offers': "Ofrece",
            'Ok': "Ok",
            'OK': "Ok",
            'OldPassword': "Contraseña Antigua",
            'OLO_General_Error': "<h1 class='warning'>¡Ups!</h1>Parece que algo salió mal. ¡Inténtalo de nuevo!<br /><br />Si el problema persiste, lo invitamos a llamar al restaurante al %storePhoneNumber% y hacer su pedido por teléfono.<br /><br />Nos disculpamos sinceramente por las molestias.<br /><span style='color:#dddddd; font-size:8px;'>noconnt</span>",
            'OnePrimAddr': "Por favor seleccione una dirección primaria.",
            'ONLINE_ORDERS_UNAVAILABLE': "La tienda no está aceptando pedidos en línea en este momento.",
            'OnlyOneDiscountAllowedAtATimeError': "Solo se permite un descuento a la vez.<br>Elimine el descuento existente para continuar.",
            'OpenOrders': "Pedidos Abiertos",
            'OpensAt': "Abre a las {time}",
            'OpenUntil': "Abierto hasta las {time}",
            'Optional': "Opcional",
            'or': "o",
            'Or': "O",
            'Order': "Pedido",
            'OrderCart': "Carrito de Pedidos",
            'OrderCenter': "Centro de Pedidos",
            'OrderComment': "Nota: Los cupones agregados en el cuadro <strong> NO </strong> se aplicarán a su pedido.",
            'OrderDateTime': "Pedido de Fecha y Hora",
            'OrderDetailItemName': "??? ??",
            'OrderDetails': "Detalles del Pedido",
            'ORDER_DISPATCHED_THIRDPARTY': "Tu pedido se enviará a través de {{provider_name}}",
            'OrderIsClosed': "El pedido está cerrado.",
            'OrderNow': "Iniciar Pedido",
            'OrderNumber': "Número de pedido",
            'OrderPlaced': "Pedido Realizado",
            "Orders": "Pedidos",
            'OrderSummary': "Resumen del Pedido",
            'OrderConfirmation': "Confirmación del Pedido",
            'OrderTime': "Tiempo del pedido",
            'OrderTotal': "Total del pedido",
            'OrderType': "Tipo de pedido",
            'OrderTypeNotOfferedAtThisLocation': "{{order_type}} no está disponible en esta ubicación.",
            'Other': "Otro",
            'OtherReason': "El motivo \"Otro\" es obligatorio.",
            'OUT_DELIVERY': "Esta dirección ha sido marcada como dirección de no entrega:<br>{{address}}",
            'OutTimeDelivery': "La dirección está en un área fuera del horario de entrega de la tienda.",
            'PaidAmount': "Monto de Pago",
            'PaidWithSavedCreditCard': "Pagado con tarjeta de crédito guardada",
            'PartialUpdateInfo': "La información parcial fue actualizada con éxito.",
            'PARTICIPATING_IN_FUNDRAISER': "Estás participando en {{fundraiser_name}}",
            'pass6Char': "Tu contraseña debe tener al menos 6 caracteres.",
            'passDigit': "Su contraseña debe contener al menos un dígito.",
            'passLetterLower': "Su contraseña debe contener al menos una letra en minúscula.",
            'passLetterUpper': "Su contraseña debe contener al menos una letra mayúscula.",
            'Passcode': "Código de Acceso",
            'Password': "Contraseña",
            'PasswordExpiredSentNewEmail': "Su contraseña ha caducado. Le enviamos un correo electrónico para restablecer su contraseña.",
            'PasswordNoMatch': "Las contraseñas no conciden.",
            'PasswordReset': "Hemos enviado un mensaje a {email}. Revise su carpeta de spam si no ha recibido el correo electrónico en 5 minutos.",
            'PasswordSaveError': "Hubo un error al actualizar su contraseña. Inténtalo de nuevo.",
            'PasswordUpdatedSuccessfully': "Su contraseña se ha actualizado correctamente.",
            'PasswordResetSuccessfully': "Su contraseña se ha restablecido correctamente.",
            'PayInStore': "Pagar en tienda",
            'Payment': "Pago",
            'PaymentMethod': "Método de Pago",
            'PaymentMethods': "Métodos de Pago",
            'PaymentTypeNotApplicableForFutureDate': "El tipo de pago seleccionado no es aplicable para pedidos con fecha futura.",
            'PayWithDollars': "Pagar con Dólares",
            'Phone': "Teléfono",
            'PHONE_ALREADY_EXISTS': "El número de teléfono ya existe.",
            'PhoneCodeIsInvalidOrExpired': "El código no es válido o ha caducado.<br>Vuelva a intentarlo.",
            'PhoneCountryCode': "Código de País del Teléfono",
            'PhoneFormat': "XX XXXX XXXX",
            'PhoneNoMatch': "Los números de teléfono que ha introducido no conciden.",
            'PickUp': "Recoger",
            'PickUpFrom': "Recoger en",
            'PickUpTime': "Hora de Recogida",
            'Pin': "Pin",
            'PlaceAnOrderToSeeYourNextMembership': "Haz un pedido para ver tu membresía",
            'PlaceAnOrderToSeeYourNextReward': "Haz un pedido para empezar a ganar recompensas",
            'PlaceOrder': "Realizar Pedido",
            'PleaseEnterAmountGreaterThan': "Por favor utilize una cantidad mayor que {{x}}",
            'PleaseEnterAmountLessThanAmountDue': "Por favor, utilize una cantidad menor del pedido de {{x}}",
            'PleaseEnterAmountWithinRangeFrom': "Por favor, utilize una cantidad dentro del rango de {{x}} a {{y}}",
            'PleaseSelectItems': "Seleccione Artículos",
            'point': "punto",
            'points': "puntos",
            'Points': "Puntos",
            'POINTS_LABEL_WITH_POINTS': "{{points}} {{points_unit}}",
            'PointsAvailable': "Puntos Disponibles",
            'PointsAwayFromNext': "puntos de distancia de su próximo",
            'PointsEarned': "Puntos de fidelidad ganados",
            'PointsLastUpdated': "Última Actualización de Puntos",
            'PointsRedeemed': "Puntos Canjeados",
            'PointsRemain': "Quedan Puntos",
            'PointsRequired': "Puntos requeridos: {{points}}",
            'POINTS_REQUIRED_LABEL': "{{points_unit}} requeridos: {{points}}",
            'PointsTo': "Puntos para ",
            'PointsToNextReward': "puntos hasta el siguiente recompensa",
            'PointsToTheNextTier': "Puntos para el siguiente nivel",
            'PoweredBy': "Energizado por",
            'PreferredCarryOutStore': "Tienda Preferida para Recoger",
            'PreferredCarryOutStoreUpdateSuccess': "Tu tienda preferida para recoger ha sido actualizada.",
            'PreferredCarryOutStoreUpdateFailure': "No se pudo actualizar tu tienda preferida para recoger.",
            'PreferredDeliveryAddress': "Dirección de Entrega Preferida",
            'PreModsNotSet': "No se han establecido premodificadores.",
            'PR': "Puerto Rico (+1)",
            'Price': "Precio",
            'Primary': "Primario",
            'privacy': "política de privacidad",
            'PrivacyPolicy': "Política de Privacidad",
            'PrivacyTerms': "He leído y acepto los ",
            'ProceedToCartToCheckout': "Proceda al carrito para pagar",
            'Profile': "Perfil",
            'ProfileWithPhoneDoesntExist': "Hubo un problema al recuperar el número de teléfono. Vuelva a intentarlo o utilice un número diferente.",
            'Promise': "Promesa",
            'PromiseTime': "Tiempo de Promesa",
            'PromosRewards': "Promociones y recompensas",
            'PromotionHasBeenRedeemed': "La promoción ha sido canjeada.",
            'PromotionHasExpired': "La promoción ha caducado.",
            'Promotions': "Coupon",
            'PromotionCode': "Código de Promoción",
            'pts': "ptos",
            'ptsEarned': "ptos ganado",
            'Quantity': "Cantidad",
            'QuantityAndAddToOrder': "Cantidad y añadir al pedido",
            'ReadAndAcceptTermsOfUseAndPrivacyPolicy': "He leído y acepto los {{terms_of_use}} y la {{privacy_policy}}.",
            'READY_FOR_PICKUP_BY': "Listo para Recoger por",
            'READY_FOR_CURBSIDE_PICKUP_BY': "Listo para Recogida en la Acera por",
            'RecentOrderHeading': "Pedido de {0}, {1}",
            'RecentOrders': "Pedidos Recientes",
            'RecentlyOrdered': "Pedidos Recientes",
            'RecentOrder_NotAllModifiersAvailable': "El/los artículo(s) {{itemNames}} han sido eliminados del pedido ya que las modificaciones exactas del pedido anterior ya no están disponibles.",
            'Redeem': "Canjear",
            'RedeemBy': "Canjear por",
            'RedeemableItems': "Los Productos Rescatables",
            'Redeemed': "Canjeado",
            'RedeemFor': "Canjear por",
            'RedeemWithPoints': "Canjear con Puntos",
            'REDEEM_WITH_POINTS_TEMPLATE': "Canjear con {{points_unit}}",
            'RedeemCurrency': "Canjear Recompensas",
            'RedeemPoints': "Canjear Puntos",
            'REDEEM_POINTS_TEMPLATE': "Canjear {{points_unit}}",
            'RedeemRewards': "Canjear Recompensas",
            'RedeemRewardHasAlreadyBeenAppliedError': "Ya se ha aplicado una recompensa de canje. br>Elimine la recompensa de canje ya aplicada.",
            'ReEmail': "Vuelva a introducir Email",
            'ReEnterExisting': "Por favor, vuelva a introducir la contraseña existente",
            'REJECT_ALL': "RECHAZO AL",
            'RemainingAmount': "Cantidad Restante:",
            'RemainingBalance': "Balance Restante",
            'RememberMe': "Recuérdame",
            'RememberMe30Days': "Recuérdame por 30 días",
            'UseBiometricLogIn': "Usar Inicio de Sesión Biométrico",
            'Remove': "Eliminar",
            'RemoveCoupon': "Eliminar Cupón",
            'RemoveFromFavorites': "Eliminar de Favoritos",
            'RemoveGiftCard': "Eliminar Tarjeta Regalo",
            'RemoveHalf': "Quitar Mitad",
            'Reorder': "Volver a Pedir",
            'RePassword': "Escriba la contraseña otra vez",
            'RepeatPassword': "Repita la Contraseña",
            'RePhone': "Vuelva a introducir teléfono",
            'RequestResetPassword': "Solicitar restablecimiento de contraseña",
            'Required': "Requerido",
            'REQUIRED_BIRTHDAY': "La fecha de nacimiento es obligatoria.",
            'RequiredField': "Campo Requerido.",
            'RequiredMonth': "El mes es obligatorio.",
            'RequiredDay': "El día es obligatorio.",
            'RequiredDefaultStore': "Debe seleccionar su tienda.",
            'ResendCode': "Reenviar Codigo",
            'ResetPass_SessionExpired_Error': "Su sesión de restablecimiento de contraseña ha expirado. Intente restablecer su contraseña nuevamente.",
            'ResetPass_Unexpected_Error': "Ocurrió un error inesperado al intentar restablecer su contraseña. Por favor, inténtelo de nuevo más tarde.<br>Error: ",
            'ResetPassword': "Restablecer Contraseña",
            'ResetPasswordSuccessful': "Su contraseña fue restablecida con éxito.",
            'RESUBSCRIBE': "Volver a suscribirse",
            'REVIEW_ORDER': "Revisa Pedido",
            'Rewards': "Recompensas",
            'RewardsActivity': "Actividad de recompensas",
            'RewardsFAQ': "Información Sobre las Recompensas",
            'RewardsGreeting': "¡Hola, {{first_name}}!",
            'RewardsKeepUnlocking': "¡Estás ganando recompensas con cada bocado! Sigue adelante para desbloquear más ofertas y beneficios deliciosos.",
            'RewardGroupItems': "Artículos del Grupo de Recompensas",
            'RewardGroupItemsHeader': "Selección de {{ reward_group_name }}",
            'Right': "Derecha",
            'RoundUp': "Redondear",
            'ROUTE_MULTI_STOPS': "La ruta del conductor puede incluir varias paradas durante el trayecto",
            'ROUTE_UNAVAILABLE': "La ruta de entrega no está disponible en este momento.",
            'Saturday': "Sábado",
            'Save': "Guardar",
            'SPECIAL_INSTRUCTIONS': "Instrucciones Especiales",
            'SameAsDel': "Igual que la Dirección de Envío",
            'Save_your_profile_changes': "¿Guardar los cambios de tu perfil?",
            'SaveCreditCardForFutureOrders': "Me gustaría guardar mi información de tarjeta de crédito para futuros pedidos.",
            'SavedAddresses': "Direcciones Guardadas",
            'SavedCards': "Tarjetas Guardadas",
            'SavingPassword': "Guardar Contraseña",
            'Savings': "Ahorros",
            'SaveNewAddress': "Guardar Nueva Dirección",
            'ScheduleForLater': "Seleccionar Hora Más Tarde",
            'Search': "Buscar",
            'SearchCityStateOrZipCode': "Buscar Ciudad, Estado o Código Postal...",
            'SearchingCities': "Buscando ciudades..",
            'SearchingZipCodes': "Buscando códigos postales..",
            'SecondHalf': "Segunda Mitad",
            'SecurityCode': "Código de Seguridad",
            'SecurityCodeCVV': "Código de Seguridad (CVV)",
            'Select': "Seleccionar",
            'SelectAnotherLocation': "Seleccione otra ubicación",
            'SelectOrderType': "Seleccione el tipo de pedido",
            'SelectAvailableTimeSlot': "Selecciona un horario entre las franjas horarias disponibles:",
            'SelectDeliveryAddress': "Seleccione Dirección de Entrega",
            'SelectItemsToAddtoOrder_Single': "Seleccione 1 artículo para agregarlo a su pedido",
            'SelectItemsToAddtoOrder_Plural': "Seleccione los artículos {{number}} para agregar a su pedido",
            'SELECTED': "Seleccionado",
            'SELECTED_MODIFIERS': "Modificadores seleccionados",
            'SelectedStore': "Tienda seleccionada",
            'SelectedTime': "Hora Seleccionada",
            'SelectedTimeNotAvailableSelectNew': "El horario seleccionado está completo.<br>Por favor, elige otro disponible.",
            'SelectedTimeReserveFailed': "No pudimos reservar ese horario.<br>Por favor, inténtalo de nuevo o elige otro horario.",
            'SelectEvent(s)': "Seleccionar Evento(s)",
            'SelectItem': "Seleccione el artículo",
            'SelectLocation': "Seleccionar<br>Tienda",
            'SelectStore': "Seleccionar Tienda",
            'SelectAddress': "Seleccionar Dirección",
            'ASSIGN_POINTS_SELECT_MEMBERS': "Selecciona el/los Miembro(s) para Asignar Puntos en Este Reto.",
            'SelectMembers(s)': "Seleccionar Miembro(s)",
            'SelectPaymentMethod': "Seleccionar Método de Pago",
            'SelectTime': "Seleccionar Hora",
            'SelectWhenYouWantToPlaceOrder': "Por favor, seleccione cuando desea su pedido.",
            'SelectSecondHalf': "Seleccionar Segunda Mitad",
            'SelectYourOrderType': "Selecciona el Tipo de Pedido",
            'SelModNoSize': "{modName} no se ofrece en {sizeName} {itemName}.",
            'SelModNoSize1': "Seleccionado ?? solo se ofrece en ???. <br/>Cambie el tamaño a ???? e intenta de nuevo.",
            'SelModNoSize2': "Seleccionado ?? no se ofrece en ???. ???? está disponible en los siguientes tamaños ?????",
            'SelSizNoMod': "Tamaño seleccionado no es válido para ",
            'SelTop': "Por favor, seleccione un topping.",
            'SendingOrder': "Enviando pedido...",
            'September': "09 - Septiembre",
            'SeptemberOnly': "Septiembre",
            'SetAsDefault': "Establecer por defecto",
            'Settings': "Configuración",
            'ShowEarlierOrders': "Mostrar pedidos anteriores",
            'Show': "Mostrar",
            'ShowMore': "Mostrar Más",
            'SignIn': "Iniciar Sesión",
            'SignInSlogan': "Comienza a ganar {{points_unit}} para <span class='highlight'>canjear</span> por tus platos <span class='highlight'>favoritos</span> del menú",
            'SignInToSeeYourFavorites': "Inicia sesión para ver tus favoritos.",
            'SignInWithApple': "Inicia Sesión con Apple",
            'SignInWithFacebook': "Inicia Sesión con Facebook",
            'SignInScreen': "Pantalla de Inicio de Sesión",
            'SignInTextPrivacyTerms': "Al seleccionar Iniciar Sesión, acepta recibir un código de acceso por mensaje de texto y acepta nuestros ",
            'SignInToUseRewards': "Inicia sesión para usar tus recompensas",
            'SignOut': "Desconectar",
            'SignOut_AreYouSure': "¿Estás seguro de que quieres cerrar sesión?",
            'SignOut_AreYouSure_Empty_EmptyCart': "¿Estás seguro de que quieres cerrar sesión?",
            'SignOut_AreYouSure_EmptyCart': "¿Estás seguro de que quieres cerrar sesión, tu cesta se vaciará?",
            'SignUp': "Regístrate",
            'SignUp2': "Regístrate",
            'SignUpScreen': "Pantalla de Registro",
            'SignUpTerms': "Al marcar esta casilla, usted reconoce haber recibido, revisado y aceptado los términos de uso y la política de privacidad.",
            'SingleUseCoupon': "Cupón de un Solo Uso",
            'SIZE': "Tamaño",
            'Size': "Tamaño",
            'Skip': "Omitir",
            'Slice': "Rebanada",
            'SmsDisclaimer': "Al proporcionar su número de teléfono, usted acepta recibir mensajes de texto automatizados de {0} sobre su cuenta, actualizaciones y promociones. Pueden aplicarse tarifas de mensajes y datos. La frecuencia varía. Responda STOP para cancelar o HELP para obtener ayuda.",
            'SmsDisclaimerTerms': "Términos",
            'SmsDisclaimerViewPrivacy': "Consulte nuestra {0}.",
            'SmsDisclaimerViewTerms': "Consulte nuestros {0}.",
            'SmsDisclaimerViewTermsAndPrivacy': "Consulte nuestros {0} y nuestra {1}.",
            'SomethingWentWrong': "Algo salió mal.",
            'spChar': "Su contraseña debe contener al menos uno de<br/>estos caracteres especiales",
            'SpecialInstructions': "Instrucciones Especiales",
            'StartOrder': "Iniciar Pedido",
            'StartOrderWithOrderType': "Iniciar {{order_type}} Pedido",
            'StartingBalance': "Balance Inicial",
            'State': "Estado",
            'StoreAddress': "Dirección de la Tienda",
            'STORE_CLOSED': "La tienda está cerrada en este momento.",
            'STORE_CLOSED_OPENS_AT': "La tienda está cerrada en este momento y abrirá a las {{time}}.",
            'StoreClosedOn': "La tienda está cerrada ??.",
            'STORE_CLOSED_HOLIDAY': "La tienda está cerrada en {{holiday_name}}. Selecciona otra fecha.",
            'STORE_CLOSING_SOON': "La tienda sigue abierta, pero cerrará en {{minutes}} minutos, lo que no deja suficiente tiempo para realizar un pedido.",
            'ONLINE_HOURS_ENDING_SOON': "La tienda dejará de aceptar pedidos en línea de {{order_type}} en {{minutes}} minutos, lo que no es tiempo suficiente para realizar un pedido.",
            'StoreDistanceAway': "{distance} {type}",
            'StoreDistanceDialogMessage': "Estas realizando un pedido para {{order_type}} en:<br>{{store_address}}.<br>La tienda seleccionada está a {{store_distance}} de tu ubicación actual!<br>¿Estás seguro de que deseas continuar con este pedido?",
            'StoreDistanceDialogMessage_StoreSelector': "{{store_address}}.<br>La tienda que seleccionaste está a {{store_distance}} de tu ubicación actual!<br>¿Estás seguro de que deseas continuar con este pedido?",
            'StoreDoesNotAcceptDeferredOnlineOrdersOn': "La tienda no acepta pedidos en línea diferidos en ??.",
            'StoreDoesNotAcceptOnlineOrdersOn': "La tienda no acepta pedidos en línea el ??.</br>Llame a la tienda al ??? para hacer su pedido por teléfono.",
            'StoreHours': "Horario de Tienda",
            'storeInfoName': "Pedido Desde:",
            'StoreIsAlreadyClosed': "La tienda ya está notificada",
            'StoreLocatorDesc': "Los servicios de ubicación están deshabilitados. Para habilitarlo, salga de esta pantalla, active los servicios de ubicación y vuelva a intentarlo. Si no, elige tu país e ingresa tu código postal.",
            'storeNotFound': "No hay tiendas dentro de 25 millas de su código postal (%zipcode%). Pruebe con un nuevo código postal en el campo de búsqueda",
            'StorePhone': "Teléfono de la Tienda",
            'StreetAddress': "Dirección de la Calle",
            'StreetAddressPlaceholder': 'Introduce tu Dirección',
            'StreetName': 'Nombre de la calle',
            'StreetNo': 'Número de calle',
            'SUBMIT': "ENTREGAR",
            'SubmitOrder': "Realizar Pedido",
            'SUBSCRIPTION_CONFIRMED': "Tu suscripción se ha confirmado correctamente.<br>Te hemos añadido a nuestra lista y nos pondremos en contacto contigo en breve.",
            'SubSurv': "Suscribirme a las encuestas por email sobre los pedidos",
            'Subtotal': "Subtotal",
            'SuccessfullyEnrolled': "Se ha inscrito con éxito en el programa de fidelización.",
            'SuccessfullyJoinedLoyaltyProgram_MsgBoxOK': "Te has unido con éxito a nuestro programa de Lealtad!",
            'SuccessfullyUpdatedProfilePage_MsgBoxOK': "Tu perfil ha sido actualizado exitosamente.",
            'SuccessfullyUpdatedProfilePageJoinedLoyalty_MsgBoxOK': "Tu perfil ha sido actualizado exitosamente.</br>Te has unido con éxito a nuestro programa de Lealtad!",
            'SuccessUpdateInfo': "Información actualizada con éxito",
            'Sunday': "Domingo",
            'SwapHalf': "Cambiar Mitad",
            'SystemCannotLocateAddress': "El sistema no puede localizar la dirección.",
            'Table': "Mesa",
            'TakeOut': "Para llevar",
            'Tax': "Impuesto",
            'TAXES': "Impuestos",
            'TemporarilyClosed': "Cerrado Temporalmente",
            'terms': "términos de uso",
            'TermsOfUse': "Términos de Uso",
            'TestBrand': "Bienvenido a {{brand}}",
            'THANK_YOU': "Gracias",
            'ThankYouFirstName': "Gracias, {{first_name}}",
            'Thursday': "Jueves",
            'tier': "nivel",
            'Time': "Tiempo",
            'TimeFrameToExpireContinue': "Tu franja horaria reservada está a punto de expirar.<br>¿Te gustaría ampliarla?",
            'Timeout': "Solicitud de espera!",
            'Tip': "Propina",
            'ToBePaidByCreditCardUponDelivery': "A pagar con tarjeta de crédito a la entrega",
            'ToBePaidInCash': "A pagar en efectivo",
            'ToChange': "Cambiar",
            'TodayHours': "Hoy",
            'TogoSupplies': "Para Llevar",
            'tomorrow': "mañana",
            'Toppings': "Ingredientes",
            'Total': "Total",
            'Tracker_FetchTrackerData_Error': "Hubo un error al recuperar su pedido. Inténtalo de nuevo.",
            'Tracker_IncorrectAccount_Error': "Este pedido no pertenece a esta cuenta. Inicie sesión con la cuenta correcta.",
            'Tracker_Status_Error': "El progreso de preparación de su pedido estará disponible a partir de las ",
            'TrackMyOrder': "Seguimiento de mi Pedido",
            'TrackOrder': "Seguimiento del Pedido",
            'TrackOrder_FetchTrackOrderData_Error': "No se pueden cargar los detalles del pedido. Por favor, inténtelo de nuevo más tarde.",
            'TrackYourOrder': "Sigue tu Pedido",
            'Transaction': "Transacción",
            'TransactionCarriedOutThrough': "Transacciones realizadas a través de:",
            'TransactionNumber': "Número de transacción",
            'TransactionNumberAbr': "Transaction No.",
            'Tuesday': "Martes",
            'UnableAutoSignIn': "No se pudo iniciar sesión automáticamente. Por favor, inicie sesión manualmente.",
            'UnableCreateAccount': "No se pudo crear la cuenta.",
            'UnableEnrolled': "No es posible inscribirse en el programa de fidelización.",
            'UnableEditProfile': "Utilice su solicitud de fidelización para actualizar el perfil. Estamos trabajando para permitir la edición aquí. Pedimos disculpas por cualquier inconveniente que esto pueda causar.",
            'UnableLogin': "No es posible entrar. Por favor, vuelva a intentarlo.",
            'UnableResetPassword': "No se ha podido restablecer su contraseña. <br/> Si es tu primera vez que visita nuestro nuevo pedidos en línea, siguen como invitado y crear un nuevo perfil de usuario al final de este pedido.",
            'UnableToDelete': "No se puede eliminar tu perfil.",
            'UnableToRetrieveCampaignCoupon': "Este código no es válido o ya ha sido canjeado.",
            'UnableToRetrieveReward': "No se puede recuperar la recompensa. Pedimos disculpas por el error.<br>Vuelva a intentarlo más tarde.",
            'UnableToRetrieveSurveyCoupon': "No se puede recuperar el cupón de la encuesta. Pedimos disculpas por el error.<br>Vuelva a intentarlo más tarde.",
            'UnableVerifyPhone': "No se puede verificar su número de teléfono.",
            'Unavailable': "No disponible",
            'UnavailableSize': "Talla no disponible",
            'UnavailableItemModifications1': "El / los siguiente(s) artículo(s) se han eliminado del pedido ya que las modificaciones exactas del pedido anterior ya no están disponibles:",
            'UnavailableItemModifications2': "Los artículos aún están disponibles para pedir, pero será necesario volver a seleccionar las modificaciones.",
            'UnexpectedError': "Ocurrió un error inesperado.",
            'UnexpectedError500': "Ocurrió un error inesperado.</br>Por favor, inténtelo de nuevo más tarde.",
            'US': "Estados Unidos (+1)",
            'UnknownAddressError': "Dirección no válida o la dirección está fuera del área de entrega de la tienda.",
            'UnknownError': "error no conocido!",
            'UNSUBSCRIBE': "Darse de baja",
            'UNSUBSCRIBE_CONFIRM_BUTTON': "Sí, darme de baja",
            'UNSUBSCRIBE_REASON_PROMPT': "Si tienes un momento, indícanos el motivo de tu baja (opcional):",
            'UNSUBSCRIBED': "Baja completada",
            'UNSUBSCRIBED_BY_MISTAKE_QUESTION': "¿Te diste de baja por equivocación?",
            'UNSUBSCRIBED_NOTICE': "Esta dirección ha sido dada de baja y ya no recibirá correos electrónicos de marketing.",
            'UNSUBSCRIBED_REMOVED_CONFIRMATION': "Has sido eliminado correctamente de nuestra lista de suscriptores y no recibirás más correos de campañas.",
            'UNSUBSCRIBED_REOPTIN_PROMPT': "Si te diste de baja por error, puedes volver a suscribirte:",
            'Update': "Actualizar",
            'NotNow': "Ahora No",
            'UpdateMyProfile': "Actualizar Mi Perfil",
            'UPGRADE': "Mejorar",
            'UppercaseOr': "O",
            'ValidOnlineOnly': "Sólo es válido en web",
            'VIEW': "Ver",
            'ViewAll': "Ver Todos",
            'ViewAllSelected': "Ver Todos los Seleccionados",
            'ViewCurrentPromotions': "Ver Promociones Actuales",
            'VIEW_DETAILS': "Ver Detalles",
            'ViewOrderDetails': "Ver Detalles del Pedido",
            'Void': "Nulo",
            'Wallet': "Cartera",
            'Wednesday': "Miércoles",
            'WELCOME': "Bienvenido a",
            'welcome': "Hola",
            'WelcomeTo': "Bienvenido a",
            'WeArePreparingOrder': "Estamos preparando tu pedido",
            'WeReceivedOrder': "Hemos recibido tu pedido",
            'Whole': "Entera",
            'XtraMiles': "La tienda seleccionada está a ?? millas de su ubicación actual!<br/>¿Estás seguro de que desea continuar con este pedido?",
            'XtraKilometers': "La tienda seleccionada está a ?? kilómetros de tu ubicación actual.<br/>¿Estás seguro de que quieres proceder con este pedido?",
            'Year': "Año",
            'Yes': "Sí",
            'YesDeleteMyProfile': "Sí, eliminar mi perfil",
            'YesIm': "Sí.",
            'YesProceed': "Sí (Continuar con el pedido)",
            'YouAreNotEnrolled': "No está inscrito en el programa de fidelización.",
            'YouAreNotSignedIn': "No has iniciado sesión!",
            'YouAreParticipatingIn': "Estas participando en ",
            'YouCanTrackYourOrderWhichWillBeDeliveredInAboutMinutes': "Puedes rastrear tu pedido, que será entregado en aproximadamente {{minutes}} minutos.",
            'YouCanTrackYourOrderWhichWillBeDeliveredAt': "Puedes rastrear tu pedido, que será entregado el {{dateAndTime}}.",
            'YouCanTrackYourOrderWhichWillBeReadyInAboutMinutes': "Puedes rastrear tu pedido, que estará listo en aproximadamente {{minutes}} minutos.",
            'YouCanTrackYourOrderWhichWillBeReadyAt': "Puedes rastrear tu pedido, que estará listo el {{dateAndTime}}.",
            'YouDontHaveAnyItemsInYourOrder': "No Tienes Ningún Producto en tu Pedido",
            'YoullReceiveEmailConfirmation': "Recibirás una confirmación por correo electrónico en {{email}}.",
            'YouMightAlsoLike': "También Te Podría Gustar...",
            "Youre": "Estás a",
            'YOUR_POINTS_AWAY_FROM_REWARD': "Estás a <b>{{points}} {{points_unit}}</b> de tu próximo <b>{{reward_name}}</b>",
            'YourInformation': "Tu Información",
            'YourItems': "Tus Productos ({{number}})",
            'YourOrderIsScheduledFor': "Su pedido está programado para {{datetime}}",
            'YourOrderDelivered': "Tu pedido ha sido entregado",
            'YOUR_OFFERS': "Tus Ofertas",
            'YOUR_CREDITS': "Sus Créditos", 
            'YourOrderOutForDelivery': "Tu pedido está listo para entrega",
            'YourOrderReadyForPickup': "Tu pedido está listo para ser recogido",
            'YourOrderWillBeDeliveredInAboutMinutes': "Tu pedido será entregado en aproximadamente {{minutes}} minutos.",
            'YourOrderWillBeDeliveredAt': "Tu pedido será entregado el {{dateAndTime}}.",
            'YourOrderWillBeReadyInAboutMinutes': "Tu pedido estará listo en aproximadamente {{minutes}} minutos.",
            'YourOrderWillBeReadyAt': "Tu pedido estará listo el {{dateAndTime}}.",
            'YourStore': "Tu tienda",
            'YouveGot': "Tienes",
            'ZENDLOGIN': "No se puede entrar. </br> Si es tu primera vez que visita nuestro nuevo pedido en línea, por favor rellene todos los campos (incluyendo la contraseña) para crear un nuevo perfil de usuario.",
            'ZIP': "Código Postal",
            'Zip': 'Código Postal',
            'ZipCode': "Código Postal",
            'ZipCodeRequired': "Se requiere código postal.",
            'ZWELCOME': "Bienvenido, </ br> Hemos actualizado nuestro sistema de pedidos online para ofrecer un servicio más rápido y seguro para usted. Esto requiere que usted se cree un nuevo perfil de usuario. Por favor, al final de este pedido complete la información solicitada y nos aseguraremos de que su perfil se actualiza de forma segura. Gracias por ser un cliente fiel !!",
            'CardholderName': "Nombre del Titular"
        }
    };
}